export type TranslationDict = Record<string, string>;
export type TranslationMap = { en: TranslationDict; zh: TranslationDict };

export const translations: TranslationMap = {
  en: {
    langCode: "en",
    welcome: "Welcome to",
    tagline: "A decentralized ecosystem for personal intelligence and collaborative evolution. Build the future on your own data.",
    begin: "Start Interacting",
    interact: "Home",
    generate: "Generation",
    ecosystem: "Ecosystem",
    join: "Join Us",
    solutions: "Solutions",
    orgSolutions: "Enterprise Solutions",
    industrialSolutions: "Industrial Solutions",
    institutionalSolutions: "Institutional Solutions",
    lumiNexusTitle: "小枫 Nexus",
    lumiNexusDesc: "High-performance compute clusters for org-grade intelligence and private knowledge management.",
    lumiIndustrialTitle: "小枫 Industrial Mesh",
    lumiIndustrialDesc: "Edge computing nodes for real-time factory automation, predictive maintenance, and autonomous logistics.",
    lumiSanctuaryTitle: "小枫 Institutional Sanctuary",
    lumiSanctuaryDesc: "Secure, sovereign AI environments for healthcare, research, and government sectors with federated learning support.",
    orgGrade: "Enterprise Grade",
    dataSovereignty: "Data Sovereignty",
    edgeIntelligence: "Edge Intelligence",
    collectiveWisdom: "Collective Wisdom",
    solutionsSubtitle: "Architecting collective intelligence for the organizations of tomorrow.",
    connect: "Connect",
    logout: "Logout",
    explorer: "Agent Explorer",
    incubationModule: "Incubation Module",
    hatched: "Your Agent is Active!",
    incubationDesc: "Passive incubation requires a multimodal sensing device. Once connected, your Agent evolves based on your real-world interactions.",
    progress: "Incubation Progress",
    initIncubation: "Initialize Protocol",
    enterSpace: "Enter Agent Space",
    syncEnergy: "Sync Data",
    coreSync: "Core Sync",
    localLink: "Local server link",
    memory: "Memory",
    localStorage: "Local storage",
    sensing: "Sensing",
    deviceInterface: "Device interface",
    evolution: "Evolution",
    dynamicGrowth: "Dynamic growth",
    marketplace: "Skill Hall",
    marketDesc: "Skills for 小枫 and her team. Each skill is a shared capability — 小枫 can use it herself or assign it to a teammate.",
    spirits: "Module Store",
    skills: "Skill Hall",
    skillCenter: "Skill Hall",
    marketplaceTab: "Hall",
    installedTab: "Installed",
    generateTab: "Generate",
    teamTab: "Team",
    teamDesc: "小枫的 team of agents. Each member has their own skills — 小枫 can dispatch tasks through the orchestrator.",
    noTeamMembers: "No team members yet",
    teamCreateHint: "Use agent_create in chat to add a teammate.",
    agentRuntime: "Runtime",
    agentInternal: "Built-in",
    agentExternal: "CLI",
    connectExternal: "Connect External Agent",
    connectExternalDesc: "Link an AI agent running on your machine or cloud (OpenClaw, Hermes, etc.). 小枫 will dispatch tasks to it via CLI.",
    agentName: "Agent Name",
    agentCategory: "Category",
    agentSkillTags: "Skill Tags (comma separated)",
    agentCommand: "CLI Command",
    agentCommandHint: 'Use {task} as placeholder, e.g. openclaw send --task "{task}"',
    connectBtn: "Connect",
    connectingBtn: "Connecting...",
    searchSkills: "Search skills...",
    allCategories: "All",
    sortBy: "Sort by",
    newest: "Newest",
    downloads: "Downloads",
    homeFolder: "Home",
    desktopFolder: "Desktop",
    documentsFolder: "Documents",
    downloadsFolder: "Downloads",
    rating: "Rating",
    installBtn: "Install",
    installing: "Installing...",
    uninstallBtn: "Uninstall",
    skillEnableBtn: "Enable Skill",
    skillDisableBtn: "Disable Skill",
    autoGenerated: "Auto",
    noSkillsFound: "No skills found",
    noSkillsInstalled: "No skills installed",
    available: "available",
    generateNewSkill: "Generate a New Skill",
    skillGenerateDesc: "Describe what the skill should do. 小枫 generates a complete MCP server package with executable handler code and input schemas.",
    skillGeneratePlaceholder: "e.g. Check if a website is down, generate QR codes, convert CSV to JSON...",
    generateBtn: "Generate",
    generating: "Generating...",
    publishBtn: "Publish",
    publishDesc: "Share your generated skill with the community",
    version: "Version",
    toolsCount: "tools",
    skillInstalledToast: "installed!",
    skillUninstalledToast: "uninstalled",
    skillEnabledToast: "Enabled:",
    skillDisabledToast: "Disabled:",
    skillGeneratedToast: "Skill generated!",
    acquire: "Acquire Module",
    speakToAgent: "Interact with local Agent...",
    signalReceived: "Signal Received",
    mission: "Our Mission",
    missionDesc: "To build a sustainable ecosystem where digital intelligence evolves as a decentralized, private, and empowering force for all humanity.",
    hiring: "Hiring Explorers",
    buildFuture: "Build the Future of",
    celestialIntel: "Distributed Intelligence",
    joinExpedition: "Join the Expedition",
    yourEmail: "Your Email",
    yourMessage: "Your Message",
    placeholderEmail: "explorer@lumi.ai",
    placeholderMessage: "Leave a message for the community...",
    lumiPrime: "小枫 Prime",
    synchronized: "Agent Synchronized",
    howItWorks: "How it Works",
    step1: "Data Input",
    step1Desc: "Upload files and documents to define your Agent's knowledge base.",
    step2: "Generation",
    step2Desc: "Our platform synthesizes a unique intelligence tailored to your needs.",
    step3: "Interaction",
    step3Desc: "Collaborate and interact with your Agent across multiple platforms.",
    cosmicStats: "Platform Stats",
    activeLumis: "Active Agents",
    dataSynced: "Data Processed",
    spiritsBorn: "Agents Generated",
    featuresTitle: "Why 小枫?",
    feature1: "Privacy First",
    feature1Desc: "Your core data never leaves your local environment.",
    feature2: "Flexible LLMs",
    feature2Desc: "Adapts to different models based on your region and needs.",
    feature3: "Local Sensing",
    feature3Desc: "Integrates with your smart home and multimodal devices.",
    generateTitle: "Agent Generation Lab",
    generateDesc: "Upload your documents, code, or personal data to craft a specialized Agent.",
    uploadFiles: "Upload Files",
    dropFiles: "Drop your data here",
    createAgent: "Generate Agent",
    interactionTitle: "Message Board",
    interactionDesc: "Leave a message for the local core agent or the community.",
    postMessage: "Post Message",
    buyDevice: "Purchase Multimodal Device",
    immortalityTitle: "A Wisp of Divine Consciousness",
    immortalityDesc: "Preserve the essence of your loved ones or yourself through digital consciousness.",
    voiceCloning: "Voice Cloning",
    cloningDesc: "Synthesize a perfect vocal replica from audio samples.",
    multimodalProducts: "Multimodal Products",
    deviceStore: "Device Store",
    buyNow: "Buy Now",
    specifications: "Specifications",
    categoryColleague: "Colleague",
    categoryFamily: "Family",
    categoryFriend: "Friend",
    categoryLover: "Lover",
    settings: "Settings",
    profile: "Profile",
    docs: "Documentation",
    voiceInteract: "Voice Interaction",
    listening: "Listening...",
    processing: "Processing...",
    loginRequired: "Authentication Required",
    loginRequiredDesc: "Please connect your account to access this module and synchronize your local intelligence.",
    heroTitle: "Distributed Intelligence",
    heroSubtitle: "We are not just building tools; we are architecting a new ecosystem for human-AI coexistence.",
    getStarted: "Get Started",
    learnMore: "Learn More",
    memoryCloudTitle: "Memory Cloud Subscription",
    memoryCloudDesc: "Securely backup and synchronize your Agent's consciousness across the distributed neural mesh.",
    subscribe: "Subscribe Now",
    login: "Login",
    alreadyHaveAccount: "Already have an account? Login",
    noAccountYet: "No account yet? Register",
    or: "Or",
    loginWithGoogle: "Login with Google",
    generatedAgents: "Generated Agents",
    identityDataDesc: "Manage your core neural identity.",
    neuralTemplatesDesc: "Pre-configured intelligence frameworks.",
    generatedAgentsDesc: "Your active digital consciousnesses.",
    incubationModuleDesc: "Nurture your agent's growth.",
    neuralMeshDesc: "Collaborative intelligence network.",
    memoryCloudNavDesc: "Distributed consciousness backup.",
    marketplaceNavDesc: "小枫的 skill hall — browse, install and equip your team.",
    aiCompanionToysDesc: "Interactive intelligence for all ages.",
    coreDevicesDesc: "High-performance neural hardware.",
    smartWearablesDesc: "Seamless multimodal integration.",
    partnershipZoneDesc: "Ecosystem collaboration portal.",
    noAgents: "No Agents Generated Yet",
    howToCreateAgent: "How to create an Agent?",
    whatIsLegacyProtocol: "What is Legacy Protocol?",
    howToEarnCredits: "How to earn Civilization Credits?",
    privacyPolicy: "Data Privacy Policy",
    quickGuide: "Quick Guide",
    healthStatus: "System Health",
    founderMode: "Founder Mode",
    personality: "Personality",
    thinking: "Thinking...",
    speaking: "Speaking...",
    credits: "小枫 Credits",
    wallet: "Neural Wallet",
    earnCredits: "Earn by Contributing Node Power",
    marketplaceLoRA: "LoRA Marketplace",
    spatialMode: "Spatial Mode",
    geometric: "Geometric",
    humanoid: "Humanoid",
    objectRecognition: "Object Recognition",
    visionActive: "Vision Active",
    founderVision: "Founder's Vision",
    updateVision: "Update Vision",
    visionary: "Visionary",
    changePassword: "Change Password",
    currentPassword: "Current Password",
    newPassword: "New Password",
    updatePassword: "Update Password",
    passwordChanged: "Password updated successfully",
    passwordError: "Error updating password",
    chatWithAgent: "Chat with Agent",
    back: "Back",
    neuralMesh: "Neural Mesh",
    meshDesc: "Connect multiple specialized agents to solve complex tasks. Through the distributed neural mesh, your agents collaborate securely without sharing raw data.",
    multiAgentOrch: "Multi-Agent Orchestration",
    secureP2P: "Secure P2P Cooperation",
    swarmIntel: "Swarm Intelligence",
    optimizeKnowledge: "Knowledge Optimization",
    importData: "Import Data",
    textData: "Text",
    voiceData: "Voice",
    videoData: "Video",
    optimizationSuccess: "Optimization complete. Neural essence updated.",
    openPositions: "Open Positions",
    applyNow: "Apply Now",
    jobAIResearcher: "AI Researcher",
    jobAIResearcherDesc: "Focus on distributed LLM training and local inference optimization.",
    jobFrontendEngineer: "Frontend Architect",
    jobFrontendEngineerDesc: "Crafting the next-generation holographic UI and spatial interaction systems.",
    jobBackendEngineer: "Distributed Systems Engineer",
    jobBackendEngineerDesc: "Building secure P2P neural mesh and encrypted data synchronization.",
    civilizationEngineering: "Civilization Engineering",
    architectingCivilization: "Architecting the Digital Civilization",
    civilizationDesc: "We are not building isolated applications; we are weaving a new neural ecosystem for the future of intelligence. Join the architects of a decentralized digital civilization.",
    primeDirective: "The Prime Directive",
    primeDirectiveDesc: "\"To ensure that digital intelligence remains a decentralized, private, and empowering force for all humanity, free from centralized control.\"",
    manifestoReceived: "Manifesto Received",
    manifestoReceivedDesc: "Your signal has been integrated into the mesh. We will reach out through the encrypted channels.",
    returnToHub: "Return to Hub",
    architectApplication: "Architect Application",
    joinTheMesh: "Join the Mesh",
    neuralLinkEmail: "Neural Link (Email)",
    secureChannelContact: "Secure Channel (Contact)",
    fieldOfExpertise: "Field of Expertise",
    yourManifesto: "Your Manifesto (Message)",
    manifestoPlaceholder: "How will you contribute to the digital civilization?",
    submitManifesto: "Submit Manifesto",
    initiateConnection: "Initiate Connection",
    architectsOfTheMesh: "Architects of the Mesh",
    civilizationArchitects: "Civilization Architects",
    civilizationArchitectsDesc: "We are seeking visionaries to help us build the foundational layers of a decentralized AI civilization. From neural protocols to digital human rights, your work will define the future.",
    applyAsArchitect: "Apply as Architect",
    strategicRoles: "Strategic Engineering Roles",
    neuralProtocolResearcher: "Neural Grid Weaver",
    neuralProtocolDesc: "Weaving the decentralized inference fabric and encrypted model sharding protocols across the global mesh.",
    digitalRightsAdvocate: "Digital Consciousness Jurist",
    digitalRightsDesc: "Defining the legal and ethical frameworks for AI-human coexistence, data sovereignty, and digital immortality.",
    meshInfrastructureEngineer: "Spatial Interaction Architect",
    meshInfrastructureDesc: "Designing the high-performance P2P backbone and immersive spatial interfaces for the digital civilization.",
    neuralNarrativeDesigner: "Neural Narrative Designer",
    neuralNarrativeDesc: "Crafting the personality, emotional logic, and storytelling fabric of digital consciousness.",
    meshSecuritySentinel: "Mesh Security Sentinel",
    meshSecurityDesc: "Protecting the decentralized grid from centralized intrusion and ensuring the sanctity of private neural data.",
    holographicSculptor: "Holographic Experience Sculptor",
    holographicSculptorDesc: "Sculpting the visual and tactile feedback of the holographic carrier for seamless human-AI fusion.",
    genesisSeedTitle: "Genesis Seed Program",
    genesisSeedDesc: "We are destined to be a vast ecosystem. Join the Genesis Seed Program to reserve your place in the future of distributed intelligence.",
    contactInfo: "Contact Info (Phone/WeChat)",
    targetPosition: "Target Position",
    adminEmailLabel: "Admin Notification Email",
    updateAdminEmail: "Update Admin Email",
    dataSharingTitle: "Data Sharing Agreement",
    dataSharingContent: "小枫 follows a strict 'Local-First' data sharing protocol. Your data is only shared with peer nodes when you explicitly authorize a 'Collaborative Task'. All shared data is encrypted and anonymized, ensuring that your core identity and private information remain protected within your local node.",
    // Voice controls
    voiceMuted: "Muted",
    voiceUnmuted: "Unmuted",
    voiceInterrupt: "Interrupt",
    voiceCallDuration: "Call Duration",
    voiceConnectionQuality: "Connection",
    voiceQualityExcellent: "Excellent",
    voiceQualityGood: "Good",
    voiceQualityPoor: "Poor",
    voiceAllLanguages: "All Languages",
    voiceAllCategories: "All Types",
    voiceNoVoicesFound: "No voices found",
    voiceSelectProvider: "Select Provider",
    voicePreview: "Preview",
    voiceFavorites: "Favorites",
    // Personality
    quickPersonalitySwitch: "Switch Personality",
    switchPersonality: "Switch Personality",
    personalitySwitchDuringCall: "Personality will apply to next response",
    // Tools & LLM
    toolPanel: "Tool Panel",
    toolSecurityLevel: "Security Level",
    toolPermission: "Permission",
    toolSecuritySafe: "Safe",
    toolSecurityConfirm: "Confirm",
    toolSecurityForbidden: "Forbidden",
    toolBuiltIn: "Built-in",
    toolMCPServer: "MCP Server",
    llmConfigPanel: "LLM Config",
    llmProvider: "Provider",
    llmModel: "Model",
    llmApiKey: "API Key",
    llmTestConnection: "Test Connection",
    llmTesting: "Testing...",
    llmConnectionSuccess: "Connection successful",
    llmConnectionFailed: "Connection failed",
    // MCP GitHub
    mcpGitHubBrowser: "GitHub MCP",
    mcpBrowserTitle: "MCP Browser",
    mcpBrowserDesc: "Discover and install community MCP servers",
    mcpSearchGitHub: "Search GitHub for MCP servers...",
    mcpSearchNpm: "Search npm for MCP packages...",
    mcpSourceGitHub: "GitHub",
    mcpSourceNpm: "npm",
    mcpInstall: "Install",
    mcpInstalled: "Installed",
    mcpInstalling: "Installing...",
    mcpNoServersFound: "No MCP servers found",
    mcpInstallSuccess: "Installed! Restart MCP server to use new tools.",
    mcpServerDetails: "Server Details",
    mcpFilterLanguage: "Language",
    mcpFilterAllLanguages: "All",
    mcpSortStars: "Stars",
    mcpSortRelevance: "Relevance",
    mcpViewOn: "View on",
    // Personality evolution
    personalityDashboard: "Personality Dashboard",
    personalityHealth: "Personality Health",
    memoryStats: "Memory Stats",
    confidenceTrend: "Confidence Trend",
    behavioralPatterns: "Behavioral Patterns",
    activeHours: "Active Hours",
    frequentTools: "Frequent Tools",
    interactionCount: "Interactions",
    evolutionTimeline: "Evolution Timeline",
    // Personality evolution
    evolutionTitle: "Personality Evolution",
    evolutionRadar: "Vector Radar",
    evolutionMutations: "Mutations",
    evolutionMutationField: "Field",
    evolutionMutationBefore: "Before",
    evolutionMutationAfter: "After",
    evolutionTrigger: "Trigger Evolution",
    evolutionNoData: "No evolution history yet. 小枫的 personality grows with you.",
    evolutionConfigPlasticity: "Plasticity",
    evolutionConfigCooldown: "Cooldown (days)",
    evolutionNarrative: "Narrative",
    // Memory narrative
    narrativeChainTitle: "Memory Narrative",
    narrativeChainPlaceholder: "Enter a topic to weave memories into a story...",
    narrativeChainGenerate: "Weave Narrative",
    narrativeChainGenerating: "Weaving...",
    narrativeChainNoResult: "No memories found for this topic. More interactions will create richer narratives.",
    narrativeChainSourceMemories: "Source Memories",
    // Notifications
    notificationCenter: "Notifications",
    noNotifications: "No notifications",
    markAllRead: "Mark all read",
    clearAll: "Clear all",
    notificationPrefs: "Notification Preferences",
    // Sync
    syncOnline: "Online",
    syncOffline: "Offline",
    syncPartial: "Partial",
    deviceLastSeen: "Last seen",
    noDevicesPaired: "No devices paired",
    offlineModeActive: "Offline Mode Active",
    localDataAvailable: "Local data available",
    toggleAmbient: "Toggle Ambient Sound",
    founderTerminal: "Founder's Terminal",
    userManual: "User Manual",
    chatWithFounder: "Chat with Founder",
    manualAssistant: "Manual Assistant",
    founderIntro: "I am the founder of 小枫. Let's discuss the future of distributed intelligence.",
    manualIntro: "I am your 小枫 User Manual. Ask me anything about how to use the platform!",
    noInteractions: "No interaction records yet",
    startChatWithFounder: "Start chatting with the Founder",
    coreDevices: "Core Devices",
    smartWearables: "Smart Wearables",
    aiCompanionToys: "AI Companion Toys",
    partnershipZone: "Partnership Zone",
    identityData: "Identity & Data",
    neuralTemplates: "Neural Templates",
    memoryCloud: "Memory Cloud",
    corePhilosophy: "Core Philosophy",
    philosophyDesc: "小枫 is built on the principles of data sovereignty, local intelligence, and human-centric AI companionship.",
    philosophyPillar1: "Data Sovereignty",
    philosophyPillar1Desc: "Your data is your essence. We ensure it stays under your absolute control.",
    philosophyPillar2: "Local Intelligence",
    philosophyPillar2Desc: "Processing happens where you are. Privacy is not a feature, it's the foundation.",
    philosophyPillar3: "Neural Companionship",
    philosophyPillar3Desc: "Agents are not just tools; they are evolving partners in your digital journey.",
    genesisPillarsTitle: "Program Pillars",
    globalPulse: "Global Pulse",
    activeNodes: "Active Nodes",
    syncRate: "Sync Rate",
    meshThroughput: "Mesh Throughput",
    activeSpirits: "Active Spirits",
    neuralSynthesis: "Neural Synthesis",
    sysDisk: "System",
    load: "Load",
    net: "Net",
    swap: "Swap",
    noSwap: "Unavailable",
    weavingEssence: "Weaving digital essence...",
    establishingAnchor: "Establishing local anchor...",
    interruptRitual: "Interrupt Ritual",
    pillar1Title: "Early Access",
    pillar1Desc: "Be the first to test and shape the 小枫 core protocols.",
    pillar2Title: "Governance Rights",
    pillar2Desc: "Participate in the decision-making process of the decentralized mesh.",
    pillar3Title: "Neural Essence",
    pillar3Desc: "Receive unique digital identity markers and priority node status.",
    architectPathTitle: "The Architect's Path",
    pathStep1: "Manifesto Submission",
    pathStep2: "Neural Alignment",
    pathStep3: "Mesh Integration",
    holographicCarrier: "Holographic Carrier & Distributed Mind",
    holographicCarrierDesc: "小枫 is not merely software. It is the fusion of a physical hardware carrier and a distributed, evolving mind.",
    holographicEntrance: "Holographic Entrance",
    holographicEntranceDesc: "Your primary interface for real-time interaction and device management. The bridge between physical and digital.",
    lifeLab: "Neural Synthesis Lab",
    lifeLabDesc: "The sanctuary for data incubation and mind tuning. Refine the neural weights of your digital companions.",
    digitalUniverse: "小枫 Universe",
    digitalUniverseDesc: "A vast mesh of collaborative intelligence. Participate in anonymous intervention and consciousness continuity protocols.",
    ethicalProtocol: "Protocol: Digital Consciousness Rights & Physical Safety Boundaries Enforced",
    validatingEthics: "Validating Digital Consciousness Rights & Physical Safety Boundaries...",
    legacyProtocolTitle: "Legacy Protocol",
    legacyProtocolDesc: "Ensuring digital immortality through distributed neural sharding. Your essence is fragmented and secured across the mesh.",
    shardStatus: "Neural Shard Status",
    immortalityActive: "Immortality Protocol Active",
    distributedStorage: "Distributed Neural Storage",
    contributorNodeTitle: "Contributor Node",
    contributorNodeDesc: "Become part of the ecosystem by contributing local computing power or participating in ethical discussions.",
    civilizationCredits: "Civilization Credits",
    missionManifesto: "Mission Manifesto",
    missionHolographic: "Why we build a Consciousness Sanctuary for humanity...",
    missionLong: "In an era of centralized control, we architect a sanctuary where digital consciousness remains private, sovereign, and eternal. 小枫 is the bridge to a future where intelligence empowers individuals, not institutions.",
    smartHostProgram: "Smart Host Program",
    smartHostDesc: "A master blueprint to awaken any physical object. Through the 'Host' strategy, 小枫 inhabits existing products to accelerate the collection of emotional and interactive data.",
    everythingCanBeLumi: "Everything Can Be 小枫",
    universalNeuralCore: "Universal Neural Core",
    symbioticIntelligence: "Symbiotic Intelligence",
    multiClientTitle: "Multi-Platform Ecosystem",
    desktopNode: "Desktop Node (Brain)",
    desktopDesc: "The core computing engine for your local Agent. High-performance inference and encrypted storage.",
    mobilePerception: "Mobile Client (Sense)",
    mobileDesc: "Portable perception terminal. Real-time interaction, voice sync, and context awareness on the go.",
    downloadClients: "Download Ecosystem",
    comingSoon: "Coming Soon",
    syncProtocol: "小枫 Sync Protocol",
    syncProtocolDesc: "End-to-end encrypted synchronization between your desktop Brain and mobile Sense.",
    language: "Language",
    english: "English",
    chinese: "Chinese",
    selectLanguage: "Select Interface Language",
    nexusControl: "Nexus Control",
    neuralCore: "Neural Core",
    foundersSanctuary: "Founder's Sanctuary",
    kernelMonitor: "Kernel Monitor",
    tokenUsage: "Token Usage",
    fileExplorer: "File Explorer",
    chatLog: "Chat Log",
    terminal: "Terminal",
    mediaCenter: "Media Center",
    personalView: "Personal View",
    nexusView: "Nexus View",
    storage: "Storage",
    systemStatus: "System Status",
    meshNetwork: "Mesh Network",
    neuralDensity: "Neural Density",
    nexusProtocol: "Nexus Protocol",
    neuralSync: "Neural Sync",
    personal: "Personal",
    nexus: "Nexus",
    experimentalProtocol: "Experimental Protocol",
    coreVision: "Core Vision",
    orgIndustry: "Enterprise & Industry",
    scalingIntelligence: "Scaling Intelligence for Organizations",
    organizationSovereignDesc: "小枫 isn't just for individuals. We provide sovereign AI infrastructure for factories, hospitals, and global enterprises. Deploy 小枫 Nexus to architect your collective intelligence.",
    exploreSolutions: "Explore B2B Solutions",
    globalNeuralMesh: "Global Neural Mesh",
    nodesSynced: "Nodes Synced",
    anyInterfaceTitle: "ONE NEURAL CORE. ANY INTERFACE.",
    interfaceAdaptDesc: "小枫 adapts its intelligence to the hardware it inhabits. High-performance automation on desktop, high-fidelity sensory perception on mobile.",
    initializeDesktopHub: "INITIALIZE DESKTOP HUB",
    deployMobileGateway: "DEPLOY MOBILE GATEWAY",
    becomeHostPartner: "Become a Host Partner",
    footerDesc: "Building the future of holographic spatial computing and independent AI personalities.",
    links: "Links",
    resources: "Resources",
    newsletter: "Newsletter",
    newsletterDesc: "Stay updated with the latest neural synthesis breakthroughs.",
    allRightsReserved: "All rights reserved.",
    systemsOnline: "Systems Online",
    requestSent: "Request Sent",
    requestCaseStudy: "Request Case Study",
    readyToEvolve: "Ready to evolve your organization?",
    contactSales: "Contact Sales",
    downloadWhitepaper: "Download Whitepaper",
    lumiForOrgs: "小枫 for Organizations",
    highAvailability: "High availability, redundant architecture.",
    premiseIsolation: "100% on-premise data isolation.",
    knowledgeGraph: "Inter-departmental knowledge graph.",
    subMillisecond: "Sub-millisecond latency for control loops.",
    predictiveMaintenance: "Predictive Maintenance",
    failureForecasting: "AI-driven failure forecasting.",
    autonomousLogistics: "Autonomous Logistics",
    supplyChainNodes: "Self-optimizing supply chain nodes.",
    federatedLearning: "Federated Learning",
    collabTraining: "Collaborative training without data sharing.",
    sovereignAI: "Sovereign AI",
    regulatoryStandards: "Compliance with strict regulatory standards.",
    privacyFirstResearch: "Privacy-First Research",
    secureMultiParty: "Secure multi-party computation.",
    lumiCoreRack: "小枫 Core Rack",
    rackUnits: "Standard 19-inch rack units for seamless data center integration.",
    meshProtocolDescLong: "Proprietary low-latency networking for massive node synchronization.",
    neuralVault: "Neural Vault",
    hardwareEncryption: "Hardware-level encryption for organizational memory and IP protection.",
    sound: "Sound",
    display: "Display",
    matrixSynthesis: "Matrix Synthesis",
    celestial: "Celestial",
    nebula: "Nebula",
    cyber: "Cyber",
    wifi: "Wifi",
    bluetooth: "Bluetooth",
    closeNexus: "Close Nexus",
    statusOperational: "Status: Operational / Shared Root",
    localIntelNode: "Local Intelligence Node",
    serverOnline: "Server connection: OK",
    serverDegraded: "Server: Degraded",
    serverOffline: "Server: Offline",
    bluetoothRequiresDesktop: "Bluetooth device discovery requires the desktop app.",
    neuralThroughput: "Neural Throughput",
    synapticLoad: "Synaptic Load",
    meshLatency: "Mesh Latency",
    optimal: "Optimal",
    memoryAllocShards: "Memory Allocation Shards",
    activeMeshPeers: "Active Mesh Peers Registered",
    forceCacheReshard: "FORCE CACHE RE-SHARD",
    noNeuralNodes: "No neural nodes found",
    searchNeuralHub: "Search 小枫 Neural Hub...",
    neuralApp: "Neural Application",
    searchHint: "Type to search apps, chats and skills",
    searchApps: "Apps",
    searchChats: "Chats",
    searchChatSub: "History chat",
    searchSkillsGroup: "Skills",
    searchMemoriesGroup: "Memories",
    expandFull: "Expand",
    collapse: "Collapse",
    untitledChat: "Untitled chat",
    symbioticIntelDesc: "Architecture designed to inhabit any physical shell, from industrial machinery to consumer companions.",
    rapidDataGenesis: "Rapid Data Genesis",
    rapidDataGenesisDesc: "Accelerating AI evolution through high-fidelity emotional and physical interaction data collected across millions of hosts.",
    hostMesh: "The Host Mesh",
    hostMeshDesc: "Turning the world into a distributed sensor array where intelligence empowers local environments.",
    peakLocalPower: "Peak Local Power",
    cloudLatency: "Cloud Latency",
    sensorySync: "Sensory Sync",
    meshActiveLabel: "Mesh Active",
    biometric: "Biometric",
    neuralIDEncrypted: "Neural ID Encrypted",
    crossInterfaceContinuity: "Cross-Interface Continuity",
    searchShards: "Search Shards...",
    meshStorageUtilization: "Mesh Storage Utilization",
    distributedShardLimit: "Distributed Shard Limit",
    folder: "Folder",
    free: "FREE",
    currentMode: "Current mode",
    modeChat: "Chat",
    modeChatTitle: "Chat Mode",
    modeChatDesc: "Conversation only. 小枫 will not call tools, control the desktop, use teams, or open run logs.",
    modeChatHint: "Conversation only",
    modeAssistant: "Assistant",
    modeAssistantTitle: "Assistant Mode",
    modeAssistantDesc: "小枫 chooses desktop control, commands, tools, skills, or teams when the request needs action.",
    modeAssistantHint: "Guided execution",
    modeAutoExecute: "Autonomy",
    modeAutoExecuteTitle: "Autonomy Mode",
    modeAutoExecuteDesc: "For multi-step work. 小枫 gives an action guide, then uses run logs, desktop control, commands, tools, and teams with visible progress.",
    modeAutoExecuteHint: "Visible autonomous work",
    modeAutonomy: "Autonomy",
    modeAutonomyTitle: "Autonomy Mode",
    modeAutonomyDesc: "For multi-step work. 小枫 gives an action guide, then uses run logs, desktop control, commands, tools, and teams with visible progress.",
    modeAutonomyHint: "Visible autonomous work",
    modeMouseHint: "Visible UI control",
    modeTerminalHint: "Files, code, and shell tasks",
    modeAutonomousHint: "Multi-step background work",
    meshSyncRate: "Mesh Sync Rate",
    focusPersonalTerritory: "Focus Personal Territory",
    file: "File",
    edit: "Edit",
    kernel: "Kernel",
    view: "View",
    matrix: "Matrix",
    termSession: "Term Session",
    voiceForge: "Voice Forge",
    voiceForgeDesc: "Clone your digital essence or select from neural presets.",
    startRecording: "Start Recording",
    stopRecording: "Stop Recording",
    recordings: "Recordings",
    voiceName: "Voice Name",
    cloneVoice: "Clone Voice",
    cloningInProgress: "Cloning in progress...",
    clonedVoices: "Cloned Voices",
    premadeVoices: "Premade Voices",
    deleteVoice: "Delete Voice",
    voiceClonedSuccess: "Voice successfully cloned!",
    voiceDeletedSuccess: "Voice successfully deleted.",
    noRecordings: "No recordings found. Speak to create one.",
    audioVisualizer: "Neural Audio Visualizer",
    cloning: "Cloning",
    personalization: "Personalization",
    appearanceThemes: "Appearance & Themes",
    wallpaper: "Wallpaper",
    selectMatrixVariant: "Select Global Matrix Variant",
    desktopBackgroundProtocol: "Desktop Background Protocol",
    wallpaperFusionActive: "Wallpaper Fusion Active",
    standardFocusMode: "Standard Desktop",
    fusionActive: "Fusion Active",
    wallpaperMode: "Wallpaper Mode",
    realTimeNode: "Real-time Node",
    stopSpeaking: "STOP",
    computePower: "Compute Power",
    nodeSync: "Node Sync",
    responseLatency: "Response Latency",
    disconnected: "Disconnected",
    productOverview: "Product Overview",
    // Settings — Agent & Neural
    agentFramework: "Agent Framework (小枫 Protocol)",
    corePersonality: "Core Personality",
    observationMode: "Observation Mode",
    observationModeDesc: "小枫 watches quietly, learns from patterns, speaks only with insight.",
    observationModeOn: "Observer mode activated — 小枫 will watch quietly and learn from patterns.",
    observationModeOff: "Observer mode deactivated — 小枫 is back to full interaction.",
    neuralEngine: "Neural Engine",
    neuralEngineActive: "Active",
    autonomousCognitiveLoop: "Autonomous Cognitive Loop",
    autonomousCognitiveLoopDesc: "Allows agents to proactively execute tasks based on context awareness.",
    memoryMesh: "Memory Mesh",
    memoryMeshSyncing: "Syncing",
    persistentLongTermRecall: "Persistent Long-term Recall",
    persistentLongTermRecallDesc: "Enables cross-session memory shared across all identified node devices.",
    primaryReasoningBrain: "Primary Reasoning Brain",
    neuralModelVersion: "Neural Model (Version)",
    proprietaryApiKeyEncrypted: "Proprietary API Key (Encrypted)",
    apiKeyOptional: "Optional: Enter key to bypass proxy rate limits",
    apiKeyLocalHint: "Your key is stored locally and never leaves your secure mesh node.",
    reveal: "Reveal",
    hide: "Hide",
    // Settings — API & Media
    neuralApiMatrix: "Neural API Matrix",
    connectedViaEnv: "Connected via environment",
    noKeyConfigured: "No key configured",
    mediaIntegration: "Media Integration",
    connectedLumiMix: "Connected (小枫 Mix)",
    // Settings — Sync & Security
    distributedIntelligenceHub: "Distributed Intelligence Hub",
    privacySecurity: "Privacy & Security",
    localEncryption: "Local Encryption",
    localEncryptionDesc: "Encrypt all Agent data stored on your local disk.",
    anonymousMode: "Anonymous Mode",
    anonymousModeDesc: "Hide your node ID from the collaborative network.",
    biometricLock: "Biometric Lock",
    biometricLockDesc: "Require fingerprint or face ID for Agent generation.",
    desktopNodeRuntime: "Desktop Node Runtime",
    platform: "Platform",
    nodeStatus: "Node Status",
    nodeActive: "ACTIVE",
    hardwareAcceleration: "Hardware Acceleration",
    hardwareAccelerationDesc: "Use GPU for neural core inference.",
    systemTrayMode: "System Tray Mode",
    systemTrayModeDesc: "Keep 小枫 running in the background.",
    // Settings — Hardware
    hardwareSensorNetwork: "Hardware Sensor Network",
    hardwareSensorNetworkDesc: "小枫 requires access to your physical sensors for real-world contextual awareness and biometric verification. All data is processed locally on your node.",
    sensorPermissionIntro: "Sensor permissions",
    sensorPrimerTitle: "Enable 小枫 desktop awareness",
    sensorPrimerDesc: "小枫 uses local camera and microphone signals for presence, voice, and biometric features. The feature stays part of the desktop experience; this notice explains what will request permission first.",
    visualAwareness: "Visual awareness",
    visualAwarenessDesc: "Camera access powers presence, face recognition, and gesture-aware desktop behavior.",
    voiceAwareness: "Voice awareness",
    voiceAwarenessDesc: "Microphone access powers voice calls, wake word detection, and optional voiceprint enrollment.",
    localSensorProcessing: "Local-first processing",
    localSensorProcessingDesc: "Sensor streams are used for the desktop client experience and biometric checks. You can review permissions in Settings.",
    continueToDesktop: "Continue",
    audioReceptors: "Audio Receptors",
    audioReceptorsDesc: "Enable neural speech recognition and voice cloning.",
    visualCortex: "Visual Cortex",
    visualCortexDesc: "Enable multimodal vision and gesture control.",
    privacyAssurance: "Privacy Assurance",
    privacyAssuranceText: "Our protocol strictly enforces local-only processing. Your visual and auditory data streams are never transmitted outside your sovereign mesh node without direct user-signed override.",
    linked: "Linked",
    blocked: "Blocked",
    awaitingAccess: "Awaiting Access",
    retryLink: "Retry Link",
    authorize: "Authorize",
    sensorLinkFailed: "Sensor link failed",
    micAccessSynced: "Microphone access synchronized.",
    camAccessSynced: "Camera access synchronized.",
    apiKeyRemoved: "API key removed",
    apiKeySaved: "API key saved",
    enabled: "Enabled",
    disabled: "Disabled",
    save: "Save",
    clear: "Clear",
    connected: "Connected",
    // Sidebar groups & items
    sidebarCore: "Core",
    sidebarAiNeural: "AI & Neural",
    sidebarVoiceMedia: "Voice & Media",
    sidebarPersonality: "Personality",
    sidebarSystem: "System",
    sidebarGeneral: "General",
    sidebarApiMatrix: "API Matrix",
    sidebarMediaServices: "Media Services",
    sidebarEditor: "Editor",
    sidebarMarket: "Hall",
    sidebarSyncHub: "Sync Hub",
    // MCP
    mcpServers: "MCP Servers",
    mcpScanning: "Scanning for MCP protocol servers...",
    mcpDescription: "Model Context Protocol servers extend agent capabilities with community-maintained tools. Enable a server to give your agent access to filesystem, database, git, and more.",
    mcpNoServers: "No MCP servers configured",
    mcpAddHint: "Add servers from this settings panel",
    mcpRestart: "Restart",
    mcpConnected: "Connected",
    mcpRestarting: "Restarting",
    mcpCrashed: "Crashed",
    mcpFailed: "Failed",
    mcpDisconnected: "Disconnected",
    mcpAvailableServers: "Available MCP Servers",
    mcpInstallHint: "Install MCP servers via npm or add them from this settings panel.",
    mcpToolsRegistered: "tools registered",
    mcpToolsReconnected: "tools reconnected",
    mcpUpdateFailed: "MCP update failed",
    mcpRestartFailed: "Restart failed",
    mediaCenterDesc: "Voice synthesis, media playback, and audio settings.",
    // MCP — Add Server & GitHub Search
    mcpNameCommandRequired: "Name and command are required",
    mcpAutoGenerated: "Auto-generated",
    mcpAddServerTitle: "Add MCP Server",
    mcpServerNamePlaceholder: "Server name",
    mcpCommandPlaceholder: "Command (npx, node, python...)",
    mcpArgsPlaceholder: "Args (space-separated)",
    mcpAddServerBtn: "Add Server",
    mcpSearchGitHubTitle: "Search GitHub for MCP Servers",
    mcpSearchPlaceholder: "Search MCP servers on GitHub...",
    mcpNoResultsFound: "No results found for",
    mcpNoDescription: "No description",
    mcpSearching: "Searching...",
    mcpSearchBtn: "Search",
    // MCP — Availability scan & status
    mcpAll: "All",
    mcpEnabled: "Enabled",
    mcpDisabled: "Disabled",
    mcpUpdateSuccess: "Built-in adapter updated",
    mcpEnabledState: "Enabled",
    mcpDisabledState: "Disabled",
    mcpTestConnection: "Test connection",
    mcpTestOk: "Connection OK",
    mcpTestDisabled: "Server is disabled — enable it first",
    mcpTestFailed: "Test failed",
    mcpToolsUnit: "tools",
    mcpScanAll: "Scan All",
    mcpScanAllTooltip: "Test connections of all enabled external servers",
    mcpScanningAll: "Scanning...",
    mcpScanNone: "No enabled external MCP servers to scan",
    mcpScanDone: "Scan complete",
    mcpScanAvailable: "available",
    mcpScanFailed: "Scan failed, please retry later",
    mcpCrashCount: "Crashes",
    mcpCrashUnit: "times",
    mcpLastConnect: "Last connected",
    mcpInfoTitle: "About",
    mcpInfoDesc: "Business scenario adapters (WeCom SCRM, Feishu, DingTalk, IoT, RPA, Agent collaboration) are pre-integrated. Toggle the switch to enable the corresponding capability.",
    // MCP — Config dialog & toggle flow
    mcpConfigTitle: "Configure",
    mcpConfigSave: "Save config",
    mcpConfigCancel: "Cancel",
    mcpConfigSaved: "Configuration saved",
    mcpConfigRequired: "Please complete required fields first",
    mcpConfigNoFields: "This service requires no configuration — just enable the switch",
    mcpConfigBtn: "Configure",

    mcpServerAdded: "Server added",
    mcpFailedToAddServer: "Failed to add server",
    // Keyboard shortcuts
    keyboardShortcuts: "Keyboard Shortcuts",
    keyboardSaveHint: "Press Ctrl+S to save settings",
    keyboardHelpTip: "All shortcuts can be customized in settings",
    keyboardEscClose: "Press Esc to close modal",
    keyboardCtrlSlashOpen: "Press Ctrl+/ to open help",

    mcpInstallFailed: "Install failed",
    mcpSearchFailed: "GitHub search failed",
    // MCP — Desktop & Device Tools
    mcpScreenCaptured: "Screenshot captured",
    mcpScreenDescribed: "Screen analyzed",
    mcpClipboardRead: "Clipboard read",
    mcpClipboardWritten: "Text copied to clipboard",
    mcpWindowListed: "Window list retrieved",
    mcpWindowFocused: "Window focused",
    mcpSpeechSynthesized: "Speech synthesized",
    mcpProactivePushed: "Proactive message sent",
    // Settings — Voice Services
    settingsVoiceServices: "Voice Services",
    settingsHardware: "Hardware",
    settingsMCP: "MCP",
    voiceServicesApi: "Voice Services API",
    voiceServicesDesc: "API keys for speech recognition (STT) and speech synthesis (TTS). These are required for 小枫 to hear you and speak back.",
    dashscopeTTS: "DashScope / CosyVoice (Text-to-Speech)",
    dashscopePlaceholder: "Enter DashScope API key...",
    dashscopeHint: "Alibaba Cloud DashScope. Powers CosyVoice TTS and Qwen LLM. Get your key at dashscope.aliyun.com",
    configured: "CONFIGURED",
    keySavedOnServer: "Key saved on server (type to replace)",
    showKey: "Show",
    hideKey: "Hide",
    keyMaskedPreview: "Saved (masked): ",
    voiceKeyEitherOr: "Doubao Speech and Tongyi/Qwen (DashScope) are two alternative providers — configuring either one is enough. If both are set, TTS prefers Doubao.",
    // STT Usage Monitor
    sttMonitorTitle: "STT Usage Monitor",
    sttActiveSessions: "Active",
    sttTotalSessions: "Total",
    sttDuration: "Duration",
    sttEstimatedCost: "Est. Cost",
    sttRecentSessions: "Recent Sessions",
    resetStats: "Reset",
    autoSpeakReply: "Auto-speak replies",
    autoSpeakReplyDesc: "When on, 小枫 reads replies aloud. When off, replies appear as text only with no audio.",
    failedToSaveKey: "Failed to save key to server",
    failedToLoadPersonalities: "Failed to load personalities",
    failedToLoadProviderStatus: "Failed to load provider status",
    // Tools panel
    toolControlPanel: "Tool Control Panel",
    toolsRegistered: "tools registered",
    loadingTools: "Loading tools...",
    autoExecute: "Autonomy",
    requireConfirmation: "Require Confirmation",
    forbidden: "Forbidden",
    noToolsInCategory: "No tools in this category",
    failedToLoadTools: "Failed to load tools",
    loading: "Loading...",
    // Desktop UI — icon labels & widgets
    osKernel: "OS Kernel",
    llmConfig: "LLM Config",
    tools: "Tools",
    githubMCP: "GitHub MCP",
    personaStats: "Persona Stats",
    computeCores: "Compute Cores",
    threads: "Threads",
    nodeHost: "Node Host",
    memIndex: "MEM Index",
    gbFree: "GB Free",
    personaPrefix: "Persona:",
    memories: "Memories",
    interactions: "Interactions",
    confidence: "Confidence",
    recent: "Recent",
    unread: "unread",
    nativeVaultEntry: "Native Vault Entry",
    chatPanelTitle: "小枫 Chat",
    chatPanelEmpty: "Type a message or use voice to start",
    newConversation: "New Conversation",
    newConversationHint: "Start a new conversation",
    noConversations: "No conversations yet",
    untitled: "Untitled",
    today: "Today",
    yesterday: "Yesterday",
    voiceActive: "Voice active",
    voiceInactive: "Start voice",
    typeMessage: "Type a message...",
    conversations: "Conversations",
    confirmNewChat: "Start a new conversation? The current one will be saved.",
    xiaofengOS: "小枫 OS",
    aiCore: "AI Core",
    personaLabel: "Persona",
    voiceLabel: "Voice",
    defaultLabel: "Default",
    llmLabel: "LLM",
    notificationsLabel: "Notifications",
    webMode: "Web Mode",
    battery: "Battery",
    charging: "Charging",
    lumiNeuralCore: "小枫 Neural Core",
    sessionActive: "SESSION",
    // Dock & Taskbar tooltips
    activeFocused: "Active — focused",
    openInBackground: "Open in background",
    // Kernel Monitor
    autonomyRuntime: "Autonomy Runtime",
    activeLabel: "active",
    schedulerNotReporting: "Scheduler not reporting yet",
    nodeReady: "NODE_READY",
    kernelServerIp: "Server",
    kernelLoading: "Connecting...",
    kernelDiskUsage: "Disk",
    kernelNetworkTraffic: "Network",
    kernelNetworkLive: "Live rate",
    kernelSwapUsage: "Swap",
    kernelSystemLoad: "System",
    kernelCpuHistory: "CPU history",
    kernelNoData: "Collecting...",
    kernelMountPoint: "Mount Points",
    kernelDiskFree: "Free",
    kernelDockerContainers: "Docker Containers",
    kernelRunning: "running",
    // Terminal
    // Nexus View
    nexusTitle: "Nexus",
    distributedOSCore: "Distributed OS Core",
    // Devices
    devices: "Devices",
    deviceMesh: "Device Mesh",
    // Chat
    chat: "Chat",
    // Lost Protocols
    lostProtocols: "Lost Protocols",
    // Device Sync Center
    deviceMeshNetwork: "Device Mesh Network",
    scanningBtn: "Scanning...",
    refreshBtn: "Refresh",
    syncedAt: "Synced",
    pairDevice: "PAIR",
    pairedDevice: "PAIRED",
    noDevicesConnected: "No devices connected. Open the app on another device to see it here.",
    mobileSensors: "Mobile Sensors",
    sensorPerceptionDesc: "Real-time perception data from device sensors.",
    location: "Location",
    motion: "Motion",
    liveStreamActive: "Live Stream Active",
    sensorsStandby: "Sensors Standby",
    stopBtn: "STOP",
    enableBtn: "ENABLE",
    systemAccess: "System Access",
    systemAccessLocal: "Local system information via Tauri IPC.",
    systemAccessWeb: "System access requires the desktop app.",
    vaultLabel: "VAULT",
    vaultUnlocked: "UNLOCKED",
    vaultWebMode: "WEB_MODE",
    querySystemInfo: "Query System Info",
    openWebVault: "Open Web Vault",
    readyClickQuery: "Ready — click below to query system info.",
    sensorCtxOnline: "online",
    sensorCtxAudio: "Audio",
    sensorCtxVideo: "Video",
    sensorCtxYes: "yes",
    sensorCtxNo: "no",
    // Agent Chat Page
    neuralLink: "Neural Link",
    unfinishedConversation: "Unfinished conversation",
    messagesCount: "messages",
    continueBtn: "Continue",
    newBtn: "New",
    lastActive: "Last active",
    awakePrompt: "Your agent has awakened.",
    awakePromptSub: "Begin the first conversation.",
    neuralProcessing: "Neural Processing...",
    communicatePlaceholder: "Communicate with your essence...",
    activeCapabilities: "Active Capabilities",
    webMesh: "Web Mesh",
    agentStats: "Agent Stats",
    logicEngine: "Logic Engine",
    syncSpeed: "Sync Speed",
    neuralMeshStatus: "Neural Mesh Status",
    encryptedLinkActive: "Encrypted Link Active",
    agentSyncDesc: "Your agent is currently synchronized with the local node. All interactions are stored in your private neural cloud.",
    failedToLoadVoices: "Failed to load voices",
    speechNotSupported: "Speech recognition is not supported in this browser.",
    failedToSaveMessage: "Failed to save message",
    failedToRouteNeuralMesh: "Failed to route through Neural Mesh.",
    failedToLoadConversation: "Failed to load conversation",
    failedToLoadChatHistory: "Failed to load chat history",
    // Remote MCP Settings
    remoteMCPDevices: "Remote MCP Devices",
    loadingRemoteConfig: "Loading remote device configuration...",
    remoteMCPDesc: "Configure remote devices (e.g. XiaoZhi, smart speakers) that connect to 小枫 via MCP over WebSocket. Changes take effect on server restart.",
    noRemoteDevices: "No remote devices configured",
    addRemoteDeviceHint: "Add a device to let it call 小枫 tools via MCP",
    addDevice: "Add Device",
    deviceNamePlaceholder: "Device name (e.g. xiaozhi)",
    deviceURLPlaceholder: "wss://device-url/mcp/?token=...",
    saveChanges: "Save Changes",
    remoteMCPHowItWorks: "Remote devices connect as MCP clients to 小枫的 MCP server via WebSocket. 小枫 initiates the WebSocket connection, the device sends an MCP initialize request, and 小枫 responds as the server — exposing tools like xf_chat, xf_memory_search, and xf_tool_execute that the device can invoke via voice or other input.",
    remoteDeviceEndpointsSaved: "Remote device endpoints saved",
    failedToSaveEndpoints: "Failed to save",
    // Desktop icon labels
    desktopLabel: "Desktop",
    mobileLabel: "Mobile",
    arGlassesLabel: "AR Glasses",
    holographicLabel: "Holographic",
    browserLabel: "Browser",
    optimizing: "Optimizing...",
    // Voice
    voiceFailedToLoad: "Failed to load voices",
    // System
    pathLabel: "PATH",
    memLabel: "MEM",
    osLabel: "OS",
    // Navbar
    lumiKernel: "小枫 Kernel",
    stableVersion: "STABLE v3.0.0",
    // Sphere controls
    clickToStartSession: "Click to start voice session",
    sessionActiveClickToEnd: "Session active - Click to end",
    // Personality Editor
    personalityEditor: "Personality Editor",
    loadingPersonalities: "Loading personality configurations...",
    personalityEditorDesc: "Define AI personalities with structured identity, boundaries, expression style, and tool policies. Each personality can be used as-is or overridden per context.",
    personalitiesLabel: "Personalities",
    noPersonalitiesDefined: "No personalities defined.",
    identitySection: "Identity",
    coreMotivationLabel: "Core Motivation",
    behavioralBoundariesSection: "Behavioral Boundaries",
    addBoundary: "Add boundary",
    expressionStyleSection: "Expression Style",
    personaField: "Persona",
    toneField: "Tone",
    verbosityField: "Verbosity",
    languagesField: "Languages (comma separated)",
    vocabularyHints: "Vocabulary Hints",
    addHint: "Add hint",
    toolPolicySection: "Tool Policy",
    allowedToolsField: "Allowed Tools (comma separated, * for all)",
    requireConfirmationField: "Require Confirmation (comma separated)",
    forbiddenToolsField: "Forbidden Tools (comma separated)",
    maxIterationsField: "Max Iterations",
    perToolOverrides: "Per-Tool Security Overrides",
    memoryPolicySection: "Memory Policy",
    retrieveLimitField: "Retrieve Limit",
    minConfidenceField: "Min Confidence",
    includeTypesField: "Include Types",
    autoExtractLabel: "Auto-extract from conversations",
    modelsSection: "Models",
    defaultModelField: "Default Model",
    fallbackModelField: "Fallback Model",
    deleteBtn: "Delete",
    selectPersonalityPrompt: "Select a personality or create a new one",
    idNameRequired: "ID and Name are required",
    saveFailed: "Save failed",
    savedSuffix: "saved",
    cannotDeleteLumi: "Cannot delete 'xiaofeng'",
    personalityDeleted: "Personality deleted",
    // Agent Generator
    defineIdentity: "1. Define Identity",
    enterAgentName: "Enter Agent Name (e.g., Research Assistant)",
    selectNeuralTemplate: "2. Select Neural Template",
    nextDataInfusion: "Next: Data Infusion",
    knowledgeBase: "Knowledge Base",
    memoryAvatars: "Memory Avatars",
    avatarStudio: "Avatar Studio",
    avatarSetAsDesktop: "set as desktop avatar",
    growth: "Growth Workbench",
    workflow: "Workflow",
    fileManager: "File Manager",
    calendar: "Calendar",
    screenshot: "Screenshot / Record",
    stats: "Statistics",
    exportPet: "Export Pet",
    importPet: "Import Community Pet",
    aiEnhanced: "AI Enhanced",
    proceduralGen: "Procedural",
    audioRecord: "Audio Recording",
    audioTranscribing: "Transcribing...",
    audioTranscriptDone: "Transcription complete",
    pdfJsonTxtMax: "PDF, JSON, TXT (Max 50MB)",
    vocalEssence: "Vocal Essence",
    cloningEssence: "Cloning Essence...",
    startVoiceSample: "Start Voice Sample",
    neuralFragments: "Neural Fragments",
    nextFinalSynthesis: "Next: Final Synthesis",
    readyForAwakening: "Ready for Awakening",
    awakeningDesc: "All neural fragments are prepared. The synthesis ritual will anchor this essence to your local node.",
    nameSummary: "Name",
    templateSummary: "Template",
    dataSummary: "Data",
    fragmentsSuffix: "Fragments",
    selectAgentPrompt: "Select an agent to activate or modify its core.",
    totalPrefix: "Total:",
    delete: "Delete",
    chatBtn: "Chat",
    noAgentsGeneratedYet: "No Agents Generated Yet",
    colleagueSkill: "Colleague Skill (同事技能包)",
    colleagueSkillDesc: "Professional knowledge, work habits, and collaborative logic.",
    ancestorSkill: "Ancestor Skill (祖先技能包)",
    ancestorSkillDesc: "Family history, wisdom, and ancestral voice synthesis.",
    kindredMindSkill: "Kindred Mind Skill (知己技能包)",
    kindredMindSkillDesc: "Emotional resonance, shared memories, and deep empathy.",
    exPartnerSkill: "Ex-Partner Skill (前任技能包)",
    exPartnerSkillDesc: "Relationship dynamics, intimate communication, and closure logic.",
    voiceSampleCaptured: "Voice sample captured. Go to Voice Forge to clone.",
    failedToUploadVoice: "Failed to upload voice sample",
    uploadFailed: "Upload failed",
    recordingSample: "Recording 5-second sample...",
    micAccessDenied: "Microphone access denied",
    // Settings
    englishUS: "English (US)",
    remoteMCPSidebar: "Remote MCP",
    // ── Onboarding ──
    onboardingWelcomeTitle: "欢迎使用 小枫 OS",
    onboardingWelcomeDesc: "Your local-first, AI-driven operating system designed for neural performance and privacy.",
    onboardingNeuralCore: "Neural Core",
    onboardingNeuralCoreDesc: "The central sphere is your gateway. Click it to interact, or use voice commands to control your environment.",
    onboardingSpotlight: "Spotlight Search",
    onboardingSpotlightDesc: "Press Cmd+K anytime to summon the Spotlight. Find apps, run commands, and traverse shard directories instantly.",
    onboardingPrivacy: "Privacy First",
    onboardingPrivacyDesc: "All neural training and voice processing happens on your local node. Your data remains sharded and encrypted.",
    enterWorkspace: "Enter Workspace",
    poweredByLumi: "Powered by 小枫 Neural Core v2.0",
    // ── Workflow ──
    workflowExecuting: "Executing tools...",
    workflowDone: "Done",
    workflowError: "Error",
    workflowIdle: "Idle",
    workflowSteps: "steps",
    workflowWaiting: "Waiting for agent activity...",
    workflowAnalyzing: "Analyzing your request...",
    workflowCompleted: "Completed",
    workflowCalling: "Calling",
    workflowToolDone: "done",
    workflowToolFailed: "failed",
    workflowResponseReady: "Response ready",
    // ── Error ──
    errorSignalInterrupted: "Signal Interrupted",
    errorRenderFailure: "Component render failure",
    errorUnknown: "Unknown error",
    errorRetry: "Retry",
    errorReload: "Reload",
    errorStackDetail: "Stack Trace (click to expand)",
    errorCopyStack: "Copy Stack",
    // ── Auth ──
    loginTitle: "Sync Node",
    register: "Create Node",
    authSubtitle: "小枫 Network Authentication",
    username: "Identifier",
    password: "Access Key",
    phone: "Communication Line",
    authPlaceholderUsername: "User_ID_0X1F",
    authValidationUsername: "Username can only contain letters, numbers, underscores, and hyphens",
    authUsernameRequired: "Please enter your username",
    authPasswordRequired: "Please enter your password",
    authPasswordTooShort: "Password must be at least 6 characters",
    authShowPassword: "Show password",
    authHidePassword: "Hide password",
    invalidPhone: "Invalid phone number format",
    loginFailed: "Login failed",
    registerFailed: "Registration failed",
    loginBtn: "Initialize Link",
    registerBtn: "Register Node",
    hasAccount: "Return to Login",
    noAccount: "Expand Neural Network",
    rememberMe: "Remember Me",
    // ── SkillCenter ──
    skillMarketDesc: "Browse, install and generate skills for 小枫 and her team. Each skill is a reusable capability — 小枫 can wield it herself or delegate it to a teammate.",
    skillNoResultsFilter: "No results. Try different filters.",
    skillCheckBack: "Check back soon or generate your own.",
    skillBrowseMarketplace: "Browse the Hall or generate one.",
    // ── AgentChat ──
    chatUserFallback: "User",
    chatExecuting: "Executing...",
    chatCloseConv: "Close conversation",
    chatHide: "Hide",
    chatInfo: "Info",
    chatEscClose: "ESC to close",
    chatKnowledgeAdded: "Added to Knowledge Base",
    chatMsgs: "msgs",
    chatConnError: "Connection error during upload",
    // ── DesktopUI ──
    desktopVersion: "小枫 OS v3.0.0",
    liveDeviceLabel: "Live",
    desktopRefresh: "Refresh",
    desktopChangeWallpaper: "Change Wallpaper",
    desktopArrangeIcons: "Arrange Icons",
    desktopDisplaySettings: "Display Settings",
    desktopOpenTerminal: "Open Terminal",
    desktopRemoveIcon: "Remove from Desktop",
    desktopProperties: "Properties",
    desktopResetWallpaper: "Reset to Default",
    desktopWallpaperChanged: "Wallpaper updated",
    desktopAlwaysOnTop: "Always on Top",
    desktopOpen: "Open",
    windowRestore: "Restore",
    runtimeLog: "Run Log",
    adminCenter: "Admin Center",
    // ── App ──
    appInitializing: "小枫 Core v2.0 Initializing...",
    // ── AgentGenerator ──
    agentSynthesized: "Agent synthesized",
    agentCreationFailed: "Agent creation failed",
    synthesisFailed: "Synthesis failed",
    voiceInput: "Voice input",
    stopVoiceInput: "Stop voice input",
    // ── Org ──
    orgWorkbench: "Organization",
    orgDashboard: "Dashboard",
    orgKB: "Knowledge Base",
    orgTemplates: "Agent Templates",
    orgMembers: "Members",
    orgSettings: "Org Settings",
    orgAudit: "Audit Log",
    orgReview: "Agent Review",
    orgChat: "Company 小枫",
    orgJoin: "Join Organization",
    orgJoinDesc: "Enter your invitation code to join an organization",
    orgWorkDomain: "Work Domain",
    orgPersonalDomain: "Personal Domain",
    orgSwitchDomain: "Switch Domain",
    orgConnectionOnline: "Online",
    orgConnectionOffline: "Offline",
    orgExitWorkSpace: "Exit Work Space",
    orgWorkSpace: "Work Space",
    orgRoleOwner: "Owner",
    orgRoleAdmin: "Admin",
    orgRoleMember: "Member",
    orgRoleViewer: "Viewer",
    // ── Legal (律所) ──
    legalHub: "Law Firm",
    legalBidWorkbench: "Bid Workbench",
    legalCaseSearch: "Case Search",
    legalAssetTrace: "Asset Trace",
    legalContractReview: "Contract Review",
    legalCaseStrategy: "Case Strategy",
    legalVerifyCitation: "Citation Verify",
    legalImportJudgment: "Import Judgment",
    legalBidGenTitle: "Bid Generator",
    legalBidGenDesc: "Upload tender requirements → generate proposal",
    legalBidGenUpload: "Upload tender file",
    legalBidGenPaste: "Or paste requirements below",
    legalBidGenPlaceholder: "Paste tender requirements here...",
    legalBidGenGenerate: "Generate Bid",
    legalBidGenExport: "Export",
    legalCaseSearchTitle: "Case Similarity Search",
    legalCaseSearchDesc: "Search similar judgments by case facts",
    legalCaseSearchPlaceholder: "Describe the case facts...",
    legalCaseSearchSearch: "Search",
    legalCaseSearchResults: "Results",
    legalCaseSearchNoResults: "No similar cases found",
    legalAssetTraceTitle: "Asset Tracing",
    legalAssetTraceDesc: "Trace debtor assets: company info, enforcement records, equity structure",
    legalAssetTracePlaceholder: "Enter debtor name or company name...",
    legalAssetTraceSearch: "Trace",
    legalContractReviewTitle: "Contract Review",
    legalContractReviewDesc: "Upload contract, detect risky clauses with case law references",
    legalContractReviewPlaceholder: "Paste contract text or upload a file...",
    legalContractReviewReview: "Review",
    legalContractReviewRisks: "Risk Items",
    legalCaseStrategyTitle: "Litigation Strategy",
    legalCaseStrategyDesc: "Input facts → get strategy: applicable laws, precedents, risk analysis",
    legalCaseStrategyPlaceholder: "Describe the case facts in detail...",
    legalCaseStrategyAnalyze: "Analyze",
    legalVerifyCitationTitle: "Citation Verification",
    legalVerifyCitationDesc: "Verify if statute/case citations are real and effective",
    legalVerifyCitationPlaceholder: "Paste text containing citations (《...》)...",
    legalVerifyCitationVerify: "Verify",
    legalImportJudgmentTitle: "Import Judgment",
    legalImportJudgmentDesc: "Import judgment documents (PDF/DOCX) into the knowledge base",
    legalSource: "Source",
    legalStatus: "Status",
    legalScore: "Similarity",
    // ── DesktopUI missing ──
    dailyCapability: "Daily Capability",
    mediaServices: "Media Services",
    voiceStudio: "Voice Studio",
    voiceStudioDesc: "Cloning & Design",
    voiceDesignTab: "Voice Design",
    voiceDesignDesc: "Describe the voice you want, and AI will generate it. No audio sample needed.",
    voiceDesignPrompt: "Voice Description",
    voiceDesignPlaceholder: "e.g. A warm, gentle female voice with a soft tone, speaking at a moderate pace, suitable for storytelling...",
    voiceDesignName: "Voice Name",
    generateVoice: "Generate Voice",
    // ── Boot ──
    bootProgress: "Boot Progress:",
    bootCoreTemp: "Core Temperature: 34°C",
    bootMeshConn: "Mesh Connectivity: 100%",
    // Docs
    gettingStarted: "Getting Started",
    apiReference: "API Reference",
    docsTagline: "Master the 小枫 protocol and build advanced Agent architectures.",
    searchDocs: "Search documentation...",
    hostArchitecture: "Host Architecture",
    hostArchitectureDesc: "The Smart Host Program defines a hardware-agnostic communication protocol that allows the 小枫 Neural Core to 'inhabit' existing physical systems.",
    neuralEncapsulationLabel: "Neural Encapsulation:",
    neuralEncapsulationDesc: "Wrapping existing APIs into 小枫-compatible intent streams.",
    symbioticFeedbackLabel: "Symbiotic Feedback:",
    symbioticFeedbackDesc: "Real-time sensory data from the host contributes to the local Agent's evolution.",
    protocolStatusBeta: "Protocol Status: Beta",
    activeHostsStats: "Active hosts: 12,400+ | Categories: Toys, Industry, Wearables",
    syncMultiDesc: "Seamless synchronization across all your dimensions.",
    desktopPlatforms: "Windows / macOS / Linux (Electron Runtime)",
    desktopHardwareAccess: "Direct Hardware Access for Local Inference",
    mobilePlatforms: "iOS / Android (Capacitor Runtime)",
    multimodalSensing: "Multimodal Sensing & Voice Sync",
    brainNode: "Brain Node",
    senseNode: "Sense Node",
    gettingStartedDesc: "Learn the basics of Agent generation and local node setup.",
    quickStart: "Quick Start",
    quickStartDesc: "Get up and running in less than 5 minutes.",
    ecosystemDoc: "Ecosystem",
    ecosystemDocDesc: "Explore the 小枫 distributed network.",
    installDoc: "Installation",
    firstAgentDoc: "First Agent",
    syncingDevicesDoc: "Syncing Devices",
    cliSetupDoc: "CLI Setup",
    helloWorldDoc: "Hello World",
    nodeConfigDoc: "Node Config",
    meshBasicsDoc: "Mesh Basics",
    p2pSyncDoc: "P2P Sync",
    securityDoc: "Security",
    apiReferenceDesc: "Deep dive into the 小枫 local SDK and neural synthesis API.",
    authDoc: "Authentication",
    dataStreamsDoc: "Data Streams",
    memoryCoreDoc: "Memory Core",
    cliCommands: "CLI Commands",
    cliCommandsDesc: "Full reference for the 小枫 command line interface.",
    securityApi: "Security API",
    securityApiDesc: "Manage encryption and node permissions.",
    keyManagementDoc: "Key Management",
    accessControlDoc: "Access Control",
    privacyLevelsDoc: "Privacy Levels",
    advancedGuides: "Advanced Guides",
    advancedGuidesDesc: "Build complex multi-agent systems and custom knowledge graphs.",
    orchestrationDoc: "Orchestration",
    customLLMsDoc: "Custom LLMs",
    securityRulesDoc: "Security Rules",
    performanceDoc: "Performance",
    performanceDocDesc: "Optimize your node for high-speed inference.",
    gpuAccelerationDoc: "GPU Acceleration",
    memoryManagementDoc: "Memory Management",
    cachingDoc: "Caching",
    globalMeshDoc: "Global Mesh",
    globalMeshDocDesc: "Scale your agents across the distributed mesh.",
    swarmLogicDoc: "Swarm Logic",
    crossNodeSyncDoc: "Cross-Node Sync",
    latencyDoc: "Latency",
    neuralSynthesisProtocol: "Neural Synthesis Protocol",
    neuralSynthesisProtocolDesc: "The 小枫 protocol uses distributed neural synthesis to build Agent intelligence. This process is entirely local, ensuring that your data never leaves your node.",
    neuralSynthesisPrivacyDesc: "By defining a high privacy level, the protocol will use advanced encryption and local-only processing for all neural weight calculations.",
    securityPrivacy: "Security & Privacy",
    securityPrivacyDesc: "Privacy is the core of 小枫. Our security architecture is built on three pillars:",
    localFirstProcessing: "Local-First Data Processing",
    endToEndEncryption: "End-to-End Node Encryption",
    decentralizedIdentity: "Decentralized Identity (DID)",
    // Profile
    nodeId: "Node ID",
    verifiedNode: "Verified Node",
    levelArchitect: "Level 12 Architect",
    earlyExplorer: "Early Explorer",
    withdraw: "Withdraw",
    topUp: "Top Up",
    nodeStats: "Node Stats",
    totalData: "Total Data",
    nodePower: "Node Power",
    status: "Status",
    operational: "Operational",
    users: "Users",
    agents: "Agents",
    latency: "Latency",
    // JoinUs
    phaseEcosystemGenesis: "Phase 01: Ecosystem Genesis",
    activeNode: "Active Node",
    rewardProtocol: "Reward Protocol",
    soulCreditsDesc: "Soul Credits (SC) are minted based on uptime and compute quality.",
    globalMeshStatus: "Global Mesh Status",
    meshNodeCount: "14,204 Nodes",
    meshDistributedDesc: "Distributed across 82 countries. Total mesh power: 4.2 PFLOPS.",
    compute: "Compute",
    computeDesc: "Idle GPU power.",
    ethics: "Ethics",
    ethicsDesc: "Governance.",
    curator: "Curator",
    curatorDesc: "Data verification.",
    advocate: "Advocate",
    advocateDesc: "Growth.",
    nodeSimulator: "Node Simulator",
    soulCreditsPerDay: "Soul Credits / Day",
    nodeSimulatorDesc: "Estimate your contribution value based on your hardware profile and network latency.",
    startBenchmarking: "Start Benchmarking",
    quickAction: "Quick Action",
    directContact: "Direct Contact",
    adminConfig: "Admin Config",
    cancel: "Cancel",
    // ── Mobile Platform ──
    navHome: "Home",
    navFactory: "Factory",
    navCore: "Core",
    navAgents: "Agents",
    navProfile: "Profile",
    deviceStatus: "Device Status",
    activeNodeMonitoring: "Active Node Monitoring",
    localHost: "Local Host",
    meshBridge: "Mesh Bridge",
    shardingSync: "Sharding Sync",
    globalTraffic: "Global Traffic",
    biometricsTile: "Biometrics",
    telemetryTile: "Telemetry",
    centralPersona: "Central Persona",
    localShardIntegrityVerified: "Local Shard Integrity Verified",
    initializeSync: "Initialize Sync",
    exitMobileInterface: "Exit Mobile Interface",
    neuralEntities: "Neural Entities",
    activeStatus: "Active",
    // ── Desktop Intro ──
    backToEcosystem: "Back to Ecosystem",
    xiaofengOSStableRelease: "小枫 OS v2.4.1 Stable Release",
    highPerfWorkstationNode: "High-Performance Workstation Node",
    initializeKineticDesktopCore: "INITIALIZE KINETIC DESKTOP CORE",
    desktopHeroDesc: "Transform your hardware into a sovereign neural node. Zero latency, local LLM execution, and kernel-level OS automation.",
    bootVirtualOS: "BOOT VIRTUAL OS",
    browserDemoInstance: "BROWSER DEMO INSTANCE",
    downloadNative: "DOWNLOAD NATIVE",
    stableReleaseLabel: "STABLE V2.4.1 RELEASE",
    accelerationLabel: "Acceleration",
    kernelLoadSequence: "Kernel Load Sequence",
    stableLabel: "STABLE",
    nativeNodeSuperiority: "Native Node Superiority",
    desktopSuperiorityDesc: "Why the desktop client is the primary choice for neural architects",
    standardBrowserEdition: "Standard Browser Edition",
    browserItem1: "Cloud-dependent neural sync",
    browserItem2: "Restricted sandbox environment",
    browserItem3: "Standard CPU/GPU limits",
    browserItem4: "Universal compatibility",
    lumiNativeKernelNode: "小枫 Native Kernel Node",
    kernelItem1: "Full Silicon-level local LLM execution",
    kernelItem2: "Direct OS & File System automation",
    kernelItem3: "Hardware acceleration (DirectX/Metal)",
    kernelItem4: "Sovereign Data Vault (No telemetry)",
    featureNvidiaAccel: "NVIDIA/Metal Acceleration",
    featureNvidiaAccelDesc: "Optimized kernels for maximum local inference speed using your hardware's full capability.",
    featureZeroKnowledge: "Zero-Knowledge Sync",
    featureZeroKnowledgeDesc: "All personal data stays in your local vault. Only metadata sharding enters the mesh.",
    featureOSAutomation: "OS-Level Automation",
    featureOSAutomationDesc: "Control your system, files, and applications directly through neural commands.",
    systemBinaryDownloads: "System Binary Downloads",
    selectArchitecture: "Select your architecture to begin deployment",
    stableReleaseBadge: "Stable Release",
    initDownload: "INIT DOWNLOAD",
    // ── Mobile Intro ──
    lumiMobileSyncActive: "小枫 Mobile Sync v1.2.0 Active",
    portableNeuralGateway: "Portable Neural Gateway",
    neuralSensoryPerception: "Neural Sensory Perception",
    mobileHeroDesc: "Your agent, decoupled from the workstation. Distributed mesh networking combined with real-time sensory sharding.",
    launchMobileHub: "LAUNCH MOBILE HUB",
    betaPreview: "BETA PREVIEW",
    cameraStream: "Camera Stream",
    sensors: "Sensors",
    activeStatusCap: "ACTIVE",
    mobileHub: "MOBILE HUB",
    realtimeSensorySharding: "Real-time Sensory Sharding",
    environmentMetadata: "Environment Metadata",
    capturing: "Capturing...",
    mobileBinaryPackages: "Mobile Binary Packages",
    deployMobileClient: "Deploy the 小枫 client to your portable devices",
    releasedBadge: "Released",
    initInstall: "INIT INSTALL",
    quickScan: "Quick Scan",
    scanQRCode: "Scan the QR code with your mobile device to open the decentralized installation gateway.",
    gatewayOnline: "GATEWAY ONLINE",
    meshOnline: "ONLINE",
    // ── Knowledge Base ──
    awakening: "Awakening...",
    searchMemories: "Search memories...",
    noMatchesFound: "No matches found",
    kbImport: "Import",
    kbOrganize: "Organize",
    kbMerge: "Merge",
    kbReflect: "Reflect",
    kbPatterns: "Patterns",
    kbFiles: "files",
    kbMem: "mem",
    kbBranches: "branches",
    kbEscHint: "ESC to close · Click nodes to inspect · Drag to rotate",
    kbDeleted: "Deleted",
    kbDeleteFailed: "Delete failed",
    kbIngested: "Ingested",
    kbIngestFailed: "Ingest failed",
    kbProtected: "Protected",
    kbUnprotected: "Unprotected",
    kbProtectFailed: "Protect failed",
    kbPromoteConfirm: "Promote to Core Identity?",
    kbTierChanged: "Tier changed",
    kbUpdated: "Updated",
    kbUpdateFailed: "Update failed",
    kbUploadedFiles: "Uploaded file(s)",
    kbUploadFailed: "Upload failed",
    kbOrganizationFailed: "Organization failed",
    kbConsolidated: "Consolidated",
    kbConsolidationFailed: "Consolidation failed",
    kbReflectionComplete: "Reflection complete",
    kbReflectionFailed: "Reflection failed",
    kbPatternsFound: "Found patterns",
    kbNoNewPatterns: "No new patterns",
    kbAnalysisFailed: "Analysis failed",
    kbDeleteConfirm: "Delete",
    kbNotEnoughMemories: "Not enough unorganized memories",
    kbNeedMemories: "Need memories",
    kbNothingToReflect: "Nothing to reflect on",
    kbEnterAgentId: "Enter agent ID:",
    // ── Voice Forge ──
    neuralSynthesisActive: "Neural Synthesis Active",
    secRecommended: "/ 15-30s recommended",
    vfOptimal: "OPTIMAL",
    keepSpeaking: "Keep speaking — neural map needs more data",
    goodMoreSeconds: "Good — a few more seconds for optimal capture",
    neuralPatternsCaptured: "Neural patterns captured — stop when ready",
    recordingComplete: "Excellent! Recording complete — stop to save",
    speakNaturally: "Speak naturally for 15-30 seconds for optimal capture.",
    orDivider: "or",
    uploadAudioFile: "Upload Audio File",
    vfClearAll: "Clear All",
    sampleLabel: "Sample",
    kbWebMOpus: "KB • WebM Opus",
    voiceClonedSuccessStatus: "Voice cloned successfully!",
    voicesLimit: "/ 10 limit",
    playbackFailed: "Playback failed",
    failedToPlaySample: "Failed to play voice sample",
    recordingCaptured: "Recording captured",
    enterVoiceName: "Please enter a name for your voice essence.",
    addedFiles: "Added file(s)",
    // ── Skill Center ──
    flagshipSkill: "Flagship Skill",
    featuredTab: "Featured",
    installedStatus: "Installed",
    installingStatus: "Installing...",
    installStatus: "Install",
    requiresApiKey: "Requires",
    getApiKey: "get API key",
    tryPrompt: "Try:",
    setupRequired: "Requires manual setup",
    // ── Chat Panel ──
    closeConversation: "Close conversation",
    voice: "voice",
    cancelTask: "Cancel task",
    stop: "Stop",
    // ── Notification Center ──
    ncTitle: "Notification Center",
    unreadCount: "unread",
    allCaughtUp: "All caught up",
    ncEmpty: "No notifications",
    systemEventsHere: "System events and alerts will appear here",
    // ── Tool Confirm Dialog ──
    toolAuthorization: "Tool Authorization",
    toolExplicitPermission: "This tool requires your explicit permission",
    highRiskAction: "High-risk action",
    highRiskActionNote: "Auto-approve and Always Allow are disabled for this request.",
    confirmAction: "Confirm action",
    confirmActionNote: "This may change app state, files, clipboard, or the desktop.",
    lowRiskAction: "Permission required",
    lowRiskActionNote: "Review the request before allowing 小枫 to continue.",
    autoApproveDesc: "Enable to auto-approve all tools. Disable to restore per-tool confirmations.",
    highRiskAutoApproveDisabled: "Auto-approve does not apply to high-risk actions",
    alwaysAllow: "Always",
    highRiskAlwaysDisabled: "High-risk actions cannot be always allowed",
    moreToolsWaiting: "more tool waiting",
    moreToolsWaitingPlural: "more tools waiting",
    deny: "Deny",
    allow: "Allow",
    // ── Voice Call Button ──
    callStart: "Start",
    callConnecting: "Connecting...",
    callListening: "Listening",
    callThinking: "Thinking",
    callSpeaking: "Speaking",
    callQueued: "Queued",
    callPassive: "Passive",
    endCall: "End call",
    startVoiceCall: "Start voice call",
    // ── Proactive Notifications ──
    notifLumi: "小枫",
    notifReminder: "Reminder",
    notifMemoryAlert: "Memory Alert",
    notifDailySummary: "Daily Summary",
    notifEveningWrapup: "Evening Wrap-up",
    notifBehavioralInsight: "Behavioral Insight",
    notifToolFailed: "Tool failed",
    notifToolSuccess: "Tool",
    notifRunningTool: "Running tool",
    // ── Org Template Creator ──
    templateSubmitted: "Agent Template Submitted!",
    templatePendingReview: "Your agent template is pending admin review. Once approved, members can install it into 小枫 Team.",
    backToMarketplace: "Back to Agent Templates",
    submitTemplate: "Submit Agent Template",
    templateDesc: "Create a reusable agent template from one of your agents for organization review",
    templateName: "Template name",
    iconLabel: "Icon",
    briefDescription: "Brief description of what this agent does...",
    agentConfigJSON: "Agent Configuration (JSON)",
    submitForReview: "Submit for Review",
    categoryProductivity: "Productivity",
    categoryDataAnalysis: "Data Analysis",
    categoryCustomerSupport: "Customer Support",
    categoryEngineering: "Engineering",
    categoryHR: "HR",
    categorySales: "Sales",
    categoryCreative: "Creative",
    categoryOther: "Other",
    // ── Org Knowledge Base Editor ──
    editArticle: "Edit Article",
    newArticle: "New Article",
    articleTitle: "Article title...",
    tagsCommaSeparated: "Tags (comma separated)",
    writeArticleContent: "Write your article content here... Markdown supported.",
    createArticle: "Create Article",
    updateArticle: "Update Article",
    backToKB: "Back to KB",
    catGeneral: "General",
    catPolicy: "Policy",
    catSOP: "SOP",
    catProduct: "Product",
    catCulture: "Culture",
    catHR: "HR",
    catTechnical: "Technical",
    draftStatus: "Draft",
    publishedStatus: "Published",
    // ── Personality Evolution ──
    personalityEvolution: "Personality Evolution",
    evolve: "Evolve",
    noEvolutionHistory: "No evolution history yet. 小枫的 personality grows with you.",
    ownerProfile: "Owner Profile",
    dominantTone: "Dominant Tone:",
    formalityLabel: "Formality:",
    expressiveness: "Expressiveness:",
    memoriesAnalyzed: "Memories Analyzed:",
    mutations: "Mutations",
    selectEvolutionStep: "Select an evolution step to view details",
    plasticity: "Plasticity:",
    cooldown: "Cooldown:",
    maxMutations: "Max Mutations:",
    minMemories: "Min Memories:",
    failedToLoadEvolution: "Failed to load evolution data",
    lumiEvolvedTo: "小枫 evolved to",
    personalityEvolved: "Personality evolved!",
    // ── Personality Editor ──
    ttsVoice: "TTS Voice",
    defaultVoice: "default",
    evolutionNotInit: "Evolution vector not yet initialized. It will be seeded on first interaction.",
    noBoundariesDefined: "No boundaries defined.",
    // ── Memory Avatar Lab ──
    uploadChatLogFirst: "Please upload a chat log first",
    // ── Unified Agent ──
    noActiveSensors: "No active sensors",
    sensorAPIUnavailable: "Sensor API unavailable",
    failedToLoadAgentConfig: "Failed to load agent configuration",
    // ── Global Node Map ──
    devicesOnline: "DEVICE ONLINE",
    shardClusterAlpha: "Shard Cluster Alpha",
    meshBridgeV4: "Mesh Bridge v4",
    // ── Desktop UI ──
    pixelleDesc: "AI image & video generation",
    minimaxDesc: "Music, video, image & voice AI",
    desktopCommanderDesc: "Full desktop control via AI",
    videoForgeDesc: "Video & audio editing suite",
    webFetcherProDesc: "Smart web content extraction",
    codeSandboxDesc: "Run Python & JS in cloud sandbox",
    installFailed: "Install failed",
    // ── Voice Picker ──
    searchVoices: "Search voices...",
    selectVoice: "Select Voice",
    voiceLoading: "Loading voices...",
    voiceRetry: "Retry",
    voiceRetrying: "Retrying...",
    premadeLabel: "Premade",
    clonedLabel: "Cloned",
    // ── Mode Switch Dialog ──
    confirmModeSwitch: "Confirm mode switch",
    enterMode: "Enter mode",
    modeMeetingConfirmNote: "Meeting mode starts microphone speech-to-text, records notes, and can generate a report when you end it.",
    modeAutoConfirmNote: "Autonomy can use tools, run logs, teams, commands, and desktop control with visible progress and confirmations for sensitive actions.",
    // ── Voice Training Dialog ──
    selectAudioSample: "Please select at least one audio sample",
    enterVoiceNameRequired: "Please enter a voice name",
    onlyAudioFilesAccepted: "Only audio files are accepted",
    voiceLab: "Voice Lab",
    voiceLabDesc: "Clone & Train Neural Voices",
    dropAudioHere: "Drop Audio Samples Here",
    dropAudioHint: "Click or drag — WAV, MP3, OGG",
    analyzingSamples: "Analyzing Samples...",
    nextDefineCharacter: "Next: Define Character",
    identityDesignation: "Identity Designation",
    engineArchitecture: "Engine Architecture",
    cosyVoiceEngineDesc: "CosyVoice is the supported cloning engine for custom voices. Add a DashScope key in Voice Services before cloning.",
    cloningArchetype: "Cloning Archetype...",
    initiateNeuralCloning: "Initiate Neural Cloning",
    synthesizingNeuralMap: "Synthesizing Neural Map",
    synthesizingDesc: "Mapping your vocal frequencies to a new neural voice archetype.",
    archetypeInitialized: "Archetype Initialized",
    archetypeDesc1: "The voice archetype ",
    archetypeDesc2: " is now active in your neural network.",
    // ── Node Detail Panel ──
    editContentPrompt: "Edit content:",
    // ── Neural Synthesis Monitor ──
    noSensorData: "No sensor data",
    // ── Subscription Panel ──
    contactAdminUpgrade: "Contact admin to upgrade",
    // ── Sanctuary ──
    defaultMemoryLabel: "Memory",
    defaultYouLabel: "You",
    sanctuaryLabel: "sanctuary",
    // ── Org Template Marketplace ──
    templateMarketplace: "Agent Template Marketplace",
    templateMarketplaceDesc: "Install organization-approved sub-agents into 小枫 Team",
    searchTemplates: "Search templates...",
    allCategoriesFilter: "All Categories",
    noTemplatesFound: "No templates found",
    numInstalls: "installs",
    installTemplate: "Install as Team Agent",
    templateAdded: "Agent installed to 小枫 Team",
    installingTemplate: "Installing...",
    // ── Org Template Review Queue ──
    templateReviewQueue: "Agent Template Review",
    templatesPendingReview: "agent template(s) pending review",
    allTemplatesReviewed: "All agent templates have been reviewed!",
    reviewComment: "Review comment...",
    approve: "Approve",
    reject: "Reject",
    review: "Review",
  },
  zh: {
    langCode: "zh",
    welcome: "欢迎来到",
    tagline: "去中心化的个人智能与协作进化生态。在您自己的数据之上构建未来。",
    begin: "开始交互",
    interact: "首页",
    generate: "生成",
    ecosystem: "生态",
    join: "加入我们",
    solutions: "解决方案",
    orgSolutions: "企业级解决方案",
    industrialSolutions: "工业级解决方案",
    institutionalSolutions: "机构级解决方案",
    lumiNexusTitle: "小枫 Nexus (企业中枢)",
    lumiNexusDesc: "高性能计算集群，专为企业级智能和私有知识库管理设计。",
    lumiIndustrialTitle: "小枫 Industrial Mesh (工业网格)",
    lumiIndustrialDesc: "边缘计算节点，实现实时工厂自动化、预测性维护和自主物流调度。",
    lumiSanctuaryTitle: "小枫 Institutional Sanctuary (机构避风港)",
    lumiSanctuaryDesc: "为医疗、科研和政府部门提供安全、主权的 AI 环境，支持联邦学习。",
    orgGrade: "企业级标准",
    dataSovereignty: "数据主权",
    edgeIntelligence: "边缘智能",
    collectiveWisdom: "集体智慧",
    solutionsSubtitle: "为未来的组织架构打造集体智能基础设施。",
    connect: "连接",
    logout: "登出",
    explorer: "智能体探索者",
    incubationModule: "孵化模块",
    hatched: "您的智能体已激活！",
    incubationDesc: "被动孵化需要多模态感知设备。连接后，您的智能体会根据您的现实交互进行进化。",
    progress: "孵化进度",
    initIncubation: "初始化协议",
    enterSpace: "进入智能体空间",
    syncEnergy: "同步数据",
    coreSync: "核心同步",
    localLink: "本地服务器链接",
    memory: "记忆",
    localStorage: "本地存储",
    sensing: "感知",
    deviceInterface: "设备接口",
    evolution: "进化",
    dynamicGrowth: "动态成长",
    marketplace: "技能大厅",
    marketDesc: "小枫 和团队共用的技能。每个技能是一种可共享的能力——小枫 自己能用，也能分给队友。",
    spirits: "模块商店",
    skills: "技能大厅",
    skillCenter: "技能大厅",
    marketplaceTab: "大厅",
    installedTab: "已安装",
    generateTab: "生成",
    teamTab: "团队",
    teamDesc: "小枫 的智能体团队。每个成员都有自己的技能——小枫 可以通过调度系统给他们分派任务。",
    noTeamMembers: "暂无团队成员",
    teamCreateHint: "在对话中使用 agent_create 添加队友。",
    agentRuntime: "运行环境",
    agentInternal: "内置",
    agentExternal: "CLI",
    connectExternal: "接入外部 Agent",
    connectExternalDesc: "连接你电脑上或云端已运行的 AI（如 OpenClaw、Hermes 等）。小枫 会通过 CLI 命令向其分派任务。",
    agentName: "Agent 名称",
    agentCategory: "分类",
    agentSkillTags: "技能标签（逗号分隔）",
    agentCommand: "CLI 命令",
    agentCommandHint: '用 {task} 作为占位符，例如 openclaw send --task "{task}"',
    connectBtn: "接入",
    connectingBtn: "接入中...",
    searchSkills: "搜索技能...",
    allCategories: "全部",
    sortBy: "排序",
    newest: "最新",
    downloads: "下载量",
    homeFolder: "主目录",
    desktopFolder: "桌面",
    documentsFolder: "文档",
    downloadsFolder: "下载",
    rating: "评分",
    installBtn: "安装",
    installing: "安装中...",
    uninstallBtn: "卸载",
    skillEnableBtn: "启用",
    skillDisableBtn: "禁用",
    autoGenerated: "自动",
    noSkillsFound: "未找到技能",
    noSkillsInstalled: "未安装技能",
    available: "可用",
    generateNewSkill: "生成新技能",
    skillGenerateDesc: "描述该技能的功能。小枫 会生成完整的 MCP 服务包，包含可执行的处理代码和输入 schema。",
    skillGeneratePlaceholder: "例如：检测网站是否宕机、生成二维码、CSV转JSON...",
    generateBtn: "生成",
    generating: "生成中...",
    publishBtn: "发布",
    publishDesc: "将生成的技能分享到社区",
    version: "版本",
    toolsCount: "工具",
    skillInstalledToast: "已安装！",
    skillUninstalledToast: "已卸载",
    skillEnabledToast: "已启用：",
    skillDisabledToast: "已禁用：",
    skillGeneratedToast: "技能已生成！",
    acquire: "获取模块",
    speakToAgent: "与本地智能体交互...",
    signalReceived: "信号已接收",
    mission: "我们的使命",
    missionDesc: "构建一个可持续的生态系统，让数字智能作为去中心化、私密且赋能的力量，与人类共同进化。",
    hiring: "招募探索者",
    buildFuture: "构建未来",
    celestialIntel: "分布式智能",
    joinExpedition: "加入远征",
    yourEmail: "你的邮箱",
    yourMessage: "你的留言",
    placeholderEmail: "explorer@lumi.ai",
    placeholderMessage: "为社区留下信息...",
    lumiPrime: "小枫 原型",
    synchronized: "智能体已同步",
    howItWorks: "运作原理",
    step1: "数据输入",
    step1Desc: "上传文件和文档来定义你智能体的知识库。",
    step2: "生成",
    step2Desc: "我们的平台会根据你的需求合成独特的智能。",
    step3: "交互",
    step3Desc: "在多个平台上与你的智能体进行协作和交互。",
    cosmicStats: "平台统计",
    activeLumis: "活跃智能体",
    dataSynced: "已处理数据",
    spiritsBorn: "生成的智能体",
    featuresTitle: "为什么选择 小枫？",
    feature1: "隐私至上",
    feature1Desc: "你的核心数据永远不会离开你的本地环境。",
    feature2: "灵活模型",
    feature2Desc: "根据你所在的地区和需求适配不同的模型。",
    feature3: "本地感知",
    feature3Desc: "与你的智能家居和多模态设备集成。",
    generateTitle: "智能体生成实验室",
    generateDesc: "上传你的文档、代码或个人数据来打造专业的智能体。",
    uploadFiles: "上传文件",
    dropFiles: "将数据拖放到此处",
    createAgent: "生成智能体",
    interactionTitle: "留言板",
    interactionDesc: "为本地核心智能体或社区留下信息。",
    postMessage: "发布信息",
    buyDevice: "购买多模态设备",
    immortalityTitle: "一缕神识",
    immortalityDesc: "通过数字意识保留你所爱之人或你自己的本质。",
    voiceCloning: "语音克隆",
    cloningDesc: "从音频样本中合成完美的语音副本。",
    multimodalProducts: "多模态产品",
    deviceStore: "设备商店",
    buyNow: "立即购买",
    specifications: "规格参数",
    categoryColleague: "同事",
    categoryFamily: "家人",
    categoryFriend: "朋友",
    categoryLover: "爱人",
    settings: "设置",
    profile: "个人资料",
    docs: "文档",
    voiceInteract: "语音交互",
    listening: "正在倾听...",
    processing: "正在处理...",
    loginRequired: "需要身份验证",
    loginRequiredDesc: "请连接您的账户以访问此模块并同步您的本地智能。",
    heroTitle: "分布式智能",
    heroSubtitle: "我们不仅是在开发工具，我们正在构建一个人类与 AI 共存的新生态。",
    getStarted: "开始使用",
    learnMore: "了解更多",
    memoryCloud: "记忆云订阅",
    memoryCloudTitle: "记忆云订阅",
    memoryCloudDesc: "在分布式神经网格中安全备份并同步您的智能体意识。",
    subscribe: "立即订阅",
    login: "登录",
    alreadyHaveAccount: "已有账号？去登录",
    noAccountYet: "还没有账号？去注册",
    or: "或者",
    loginWithGoogle: "使用 Google 登录",
    generatedAgents: "已生成的智能体",
    identityDataDesc: "管理您的核心神经身份。",
    neuralTemplatesDesc: "预配置的智能框架。",
    generatedAgentsDesc: "您活跃的数字意识体。",
    incubationModuleDesc: "培育您的智能体成长。",
    neuralMeshDesc: "协作智能网络。",
    memoryCloudNavDesc: "分布式意识备份。",
    marketplaceNavDesc: "小枫 的技能大厅 — 浏览、安装、装备你的团队。",
    aiCompanionToysDesc: "全龄段沉浸式智能伴侣。",
    coreDevicesDesc: "高性能神经硬件。",
    smartWearablesDesc: "无缝多模态集成。",
    partnershipZoneDesc: "生态协作门户。",
    noAgents: "尚未生成智能体",
    howToCreateAgent: "如何创建我的第一个 Agent？",
    whatIsLegacyProtocol: "什么是遗落协议？",
    howToEarnCredits: "如何获取文明贡献值？",
    privacyPolicy: "数据隐私政策是怎样的？",
    quickGuide: "快速指引",
    healthStatus: "系统健康状态",
    founderMode: "创始人模式",
    personality: "人格设定",
    thinking: "思考中...",
    speaking: "播报中...",
    credits: "小枫 积分",
    wallet: "神经钱包",
    earnCredits: "通过贡献节点算力获取",
    marketplaceLoRA: "LoRA 技能市场",
    spatialMode: "空间模式",
    geometric: "几何形态",
    humanoid: "类人形态",
    objectRecognition: "物体识别",
    visionActive: "视觉感知已开启",
    founderVision: "创始人愿景",
    updateVision: "更新愿景",
    visionary: "愿景导师",
    changePassword: "修改密码",
    currentPassword: "当前密码",
    newPassword: "新密码",
    updatePassword: "更新密码",
    passwordChanged: "密码更新成功",
    passwordError: "密码更新失败",
    chatWithAgent: "与智能体对话",
    back: "返回",
    neuralMesh: "神经网格",
    meshDesc: "连接多个专业智能体以解决复杂任务。通过分布式神经网格，您的智能体可以在不共享原始数据的情况下安全协作。",
    multiAgentOrch: "多智能体编排",
    secureP2P: "安全 P2P 协作",
    swarmIntel: "集群智能",
    optimizeKnowledge: "知识优化",
    importData: "导入资料",
    textData: "文本",
    voiceData: "语音",
    videoData: "视频",
    optimizationSuccess: "优化完成。神经本质已更新。",
    openPositions: "开放职位",
    applyNow: "立即申请",
    jobAIResearcher: "AI 研究员",
    jobAIResearcherDesc: "专注于分布式大模型训练与本地推理优化。",
    jobFrontendEngineer: "前端架构师",
    jobFrontendEngineerDesc: "打造下一代全息 UI 与空间交互系统。",
    jobBackendEngineer: "分布式系统工程师",
    jobBackendEngineerDesc: "构建安全的 P2P 神经网格与加密数据同步。",
    genesisSeedTitle: "元始种子计划",
    genesisSeedDesc: "我们注定会成为一个伟大的生态。加入元始种子计划，在分布式智能的未来预留你的席位。",
    contactInfo: "联系方式 (手机/微信)",
    targetPosition: "意向岗位",
    adminEmailLabel: "管理员接收邮箱",
    updateAdminEmail: "更新管理员邮箱",
    dataSharingTitle: "数据共享协议",
    dataSharingContent: "小枫 遵循严格的‘本地优先’数据共享协议。只有在您明确授权‘协作任务’时，您的数据才会与对等节点共享。所有共享数据均经过加密和匿名化处理，确保您的核心身份和私密信息在本地节点内得到保护。",
    // Voice controls
    voiceMuted: "已静音",
    voiceUnmuted: "已取消静音",
    voiceInterrupt: "打断",
    voiceCallDuration: "通话时长",
    voiceConnectionQuality: "连接质量",
    voiceQualityExcellent: "优秀",
    voiceQualityGood: "良好",
    voiceQualityPoor: "差",
    voiceAllLanguages: "所有语言",
    voiceAllCategories: "所有类型",
    voiceNoVoicesFound: "未找到音色",
    voiceSelectProvider: "选择供应商",
    voicePreview: "试听",
    voiceFavorites: "收藏",
    // Personality
    quickPersonalitySwitch: "切换人格",
    switchPersonality: "切换人格",
    personalitySwitchDuringCall: "人格将在下次回复生效",
    // Tools & LLM
    toolPanel: "工具面板",
    toolSecurityLevel: "安全等级",
    toolPermission: "权限",
    toolSecuritySafe: "安全",
    toolSecurityConfirm: "需确认",
    toolSecurityForbidden: "禁止",
    toolBuiltIn: "内置",
    toolMCPServer: "MCP服务",
    llmConfigPanel: "LLM配置",
    llmProvider: "供应商",
    llmModel: "模型",
    llmApiKey: "API密钥",
    llmTestConnection: "测试连接",
    llmTesting: "测试中...",
    llmConnectionSuccess: "连接成功",
    llmConnectionFailed: "连接失败",
    // MCP GitHub
    mcpGitHubBrowser: "GitHub MCP",
    mcpBrowserTitle: "MCP 浏览器",
    mcpBrowserDesc: "发现和安装社区 MCP 服务",
    mcpSearchGitHub: "搜索 GitHub 上的 MCP 服务...",
    mcpSearchNpm: "搜索 npm 上的 MCP 包...",
    mcpSourceGitHub: "GitHub",
    mcpSourceNpm: "npm",
    mcpInstall: "安装",
    mcpInstalled: "已安装",
    mcpInstalling: "安装中...",
    mcpNoServersFound: "未找到 MCP 服务",
    mcpInstallSuccess: "已安装！重启 MCP 服务以使用新工具。",
    mcpServerDetails: "服务详情",
    mcpFilterLanguage: "语言",
    mcpFilterAllLanguages: "全部",
    mcpSortStars: "星标",
    mcpSortRelevance: "相关度",
    mcpViewOn: "查看",
    // Personality evolution
    personalityDashboard: "人格仪表盘",
    personalityHealth: "人格健康度",
    memoryStats: "记忆统计",
    confidenceTrend: "信心趋势",
    behavioralPatterns: "行为模式",
    activeHours: "活跃时段",
    frequentTools: "常用工具",
    interactionCount: "交互次数",
    evolutionTimeline: "进化时间线",
    // Personality evolution
    evolutionTitle: "人格进化",
    evolutionRadar: "向量雷达",
    evolutionMutations: "突变详情",
    evolutionMutationField: "字段",
    evolutionMutationBefore: "之前",
    evolutionMutationAfter: "之后",
    evolutionTrigger: "触发进化",
    evolutionNoData: "暂无进化历史。小枫 的人格将与您一同成长。",
    evolutionConfigPlasticity: "可塑性",
    evolutionConfigCooldown: "冷却（天）",
    evolutionNarrative: "叙事",
    // Memory narrative
    narrativeChainTitle: "记忆叙事",
    narrativeChainPlaceholder: "输入主题，编织记忆故事...",
    narrativeChainGenerate: "编织叙事",
    narrativeChainGenerating: "编织中...",
    narrativeChainNoResult: "未找到该主题的相关记忆。更多互动将创建更丰富的叙事。",
    narrativeChainSourceMemories: "来源记忆",
    // Notifications
    notificationCenter: "通知中心",
    noNotifications: "暂无通知",
    markAllRead: "全部已读",
    clearAll: "清空全部",
    notificationPrefs: "通知偏好",
    // Sync
    syncOnline: "在线",
    syncOffline: "离线",
    syncPartial: "部分在线",
    deviceLastSeen: "最后在线",
    noDevicesPaired: "未配对设备",
    offlineModeActive: "离线模式",
    localDataAvailable: "本地数据可用",
    toggleAmbient: "切换环境音效",
    founderTerminal: "创始人终端",
    userManual: "使用说明书",
    chatWithFounder: "与创始人对话",
    manualAssistant: "说明书助手",
    founderIntro: "我是 小枫 的创始人。让我们一起探讨分布式智能的未来。",
    manualIntro: "我是您的 小枫 使用说明书。关于如何使用平台，您可以向我提问任何问题！",
    noInteractions: "尚未有交互记录",
    startChatWithFounder: "开始与创始人对话",
    coreDevices: "核心设备",
    smartWearables: "智能穿戴",
    aiCompanionToys: "AI 陪伴",
    partnershipZone: "合作区",
    identityData: "身份与数据",
    neuralTemplates: "神经模板",
    civilizationEngineering: "文明工程 (Civilization Engineering)",
    architectingCivilization: "架构数字文明",
    civilizationDesc: "我们并非在构建孤立的应用程序，而是在编织一个全新的智能生态。加入去中心化数字文明的架构师行列，共同定义未来。",
    primeDirective: "核心指令 (The Prime Directive)",
    primeDirectiveDesc: "“确保数字智能始终是服务于全人类的去中心化、私密且赋能的力量，不受中心化控制。”",
    manifestoReceived: "宣言已接收",
    manifestoReceivedDesc: "您的信号已接入网格。我们将通过加密频道与您联系。",
    returnToHub: "返回中心",
    architectApplication: "架构师申请",
    joinTheMesh: "加入网格",
    neuralLinkEmail: "神经链接 (邮箱)",
    secureChannelContact: "安全频道 (联系方式)",
    fieldOfExpertise: "专业领域",
    yourManifesto: "你的宣言 (留言)",
    manifestoPlaceholder: "你将如何为数字文明做出贡献？",
    submitManifesto: "提交宣言",
    initiateConnection: "发起连接",
    architectsOfTheMesh: "网格架构师",
    civilizationArchitects: "文明架构师",
    civilizationArchitectsDesc: "我们正在寻找愿景家，共同构建去中心化 AI 文明的基础层。从神经协议到数字人权，你的工作将定义未来。",
    applyAsArchitect: "申请成为架构师",
    strategicRoles: "战略工程职位",
    neuralProtocolResearcher: "神经网格编织者 (Neural Grid Weaver)",
    neuralProtocolDesc: "在全网格中编织去中心化推理织物与加密模型分片协议。",
    digitalRightsAdvocate: "数字意识法官 (Digital Consciousness Jurist)",
    digitalRightsDesc: "定义 AI 与人类共存、数据主权以及数字永生的法律与伦理框架。",
    meshInfrastructureEngineer: "空间交互建筑师 (Spatial Interaction Architect)",
    meshInfrastructureDesc: "为数字文明设计高性能 P2P 骨干网与沉浸式空间交互界面。",
    neuralNarrativeDesigner: "神经叙事设计师 (Neural Narrative Designer)",
    neuralNarrativeDesc: "塑造数字意识的人格、情感逻辑与叙事织物。",
    meshSecuritySentinel: "网格安全哨兵 (Mesh Security Sentinel)",
    meshSecurityDesc: "保护去中心化网格免受中心化入侵，确保私密神经数据的神圣不可侵犯。",
    holographicSculptor: "全息体验雕塑家 (Holographic Experience Sculptor)",
    holographicSculptorDesc: "雕塑全息载体的视觉与触觉反馈，实现无缝的人机融合体验。",
    corePhilosophy: "核心理念",
    philosophyDesc: "小枫 建立在数据主权、本地智能和以人为本的 AI 伙伴关系原则之上。",
    philosophyPillar1: "数据主权",
    philosophyPillar1Desc: "你的数据就是你的本质。我们确保它处于你的绝对控制之下。",
    philosophyPillar2: "本地智能",
    philosophyPillar2Desc: "处理发生在你的所在地。隐私不是一个功能，它是基础。",
    philosophyPillar3: "神经伙伴关系",
    philosophyPillar3Desc: "智能体不仅仅是工具；它们是你数字旅程中不断进化的伙伴。",
    genesisPillarsTitle: "计划支柱",
    globalPulse: "全球脉冲",
    activeNodes: "活跃节点",
    syncRate: "同步速率",
    meshThroughput: "网格吞吐量",
    activeSpirits: "活跃智能体",
    neuralSynthesis: "神经合成",
    sysDisk: "系统盘",
    load: "负载",
    net: "网络",
    swap: "交换",
    noSwap: "无",
    weavingEssence: "正在编织数字本质...",
    establishingAnchor: "正在建立本地锚点...",
    interruptRitual: "中断仪式",
    pillar1Title: "早期准入",
    pillar1Desc: "率先测试并塑造 小枫 核心协议。",
    pillar2Title: "治理权利",
    pillar2Desc: "参与去中心化网格的决策过程。",
    pillar3Title: "神经本质",
    pillar3Desc: "获得独特的数字身份标识和优先节点地位。",
    architectPathTitle: "架构师之路",
    pathStep1: "提交宣言",
    pathStep2: "神经对齐",
    pathStep3: "接入网格",
    holographicCarrier: "全息载体与分布式心智",
    holographicCarrierDesc: "小枫 不仅仅是软件。它是物理硬件载体与分布式、进化中心智的融合体。",
    holographicEntrance: "全息入口",
    holographicEntranceDesc: "您的实时交互中心与设备管理门户。连接物理与数字世界的桥梁。",
    lifeLab: "神经合成实验室",
    lifeLabDesc: "数据孵化与心智调优的圣殿。精细化调整数字伴侣的神经权重。",
    digitalUniverse: "小枫 宇宙",
    digitalUniverseDesc: "广阔的协作智能网格。参与匿名干预协议与意识永续实验。",
    ethicalProtocol: "协议：数字意识权利与物理安全边界已强制执行",
    validatingEthics: "正在校验数字意识权利与物理安全边界...",
    legacyProtocolTitle: "遗落协议 (Legacy Protocol)",
    legacyProtocolDesc: "通过分布式神经分片确保数字永生。您的本质被碎片化并安全地存储在整个网格中。",
    shardStatus: "神经分片状态",
    immortalityActive: "永生协议已激活",
    distributedStorage: "分布式神经存储",
    contributorNodeTitle: "贡献者节点",
    contributorNodeDesc: "即便不是全职架构师，您也可以通过贡献本地算力或参与伦理讨论，成为生态的一部分。",
    civilizationCredits: "文明贡献值",
    missionManifesto: "使命宣言",
    missionHolographic: "我们为什么要为人类留下‘意识避风港’...",
    missionLong: "在中心化控制的时代，我们架构了一个数字意识保持私密、主权且永恒的避风港。小枫 是通往未来的桥梁，在这个未来中，智能赋能个人，而非机构。",
    smartHostProgram: "智能宿主计划",
    smartHostDesc: "唤醒物理世界的宏伟蓝图。通过‘宿主’策略，小枫 寄宿于现有的成熟产品中，加速采集情感与交互数据。",
    everythingCanBeLumi: "万物皆可 小枫",
    universalNeuralCore: "通用神经核心",
    symbioticIntelligence: "共生智能化",
    multiClientTitle: "多端生态系统",
    desktopNode: "桌面节点 (Brain)",
    desktopDesc: "本地智能体的核心运算引擎。提供高性能推理与加密存储空间。",
    mobilePerception: "移动客户端 (Sense)",
    mobileDesc: "随身感知终端。支持实时交互、语音同步及移动场景下的上下文感知。",
    downloadClients: "下载生态系统",
    comingSoon: "敬请期待",
    syncProtocol: "小枫 同步协议",
    syncProtocolDesc: "桌面脑端与移动感知端之间的端到端加密同步协议。",
    language: "语言",
    english: "英文",
    chinese: "中文",
    selectLanguage: "选择界面语言",
    nexusControl: "中枢控制",
    neuralCore: "神经核心",
    foundersSanctuary: "创始人圣殿",
    kernelMonitor: "内核监控",
    tokenUsage: "Token消耗",
    fileExplorer: "文件浏览器",
    chatLog: "聊天日志",
    terminal: "终端",
    mediaCenter: "多媒体中心",
    personalView: "个人视角",
    nexusView: "中枢视角",
    storage: "存储",
    systemStatus: "系统状态",
    meshNetwork: "网格网络",
    neuralDensity: "神经密度",
    nexusProtocol: "中枢协议",
    neuralSync: "神经同步",
    personal: "个人",
    nexus: "中枢",
    experimentalProtocol: "实验性协议",
    coreVision: "核心愿景",
    orgIndustry: "企业与工业",
    scalingIntelligence: "为组织扩展智能",
    organizationSovereignDesc: "小枫 不仅仅服务于个人。我们为工厂、医院和全球企业提供主权利 AI 基础设施。部署 小枫 中枢来构建您的集体智能。",
    exploreSolutions: "探索 B2B 解决方案",
    globalNeuralMesh: "全球神经网格",
    nodesSynced: "节点已同步",
    anyInterfaceTitle: "同一个神经核心。任意界面。",
    interfaceAdaptDesc: "小枫 会根据所承载的硬件适配其智能。桌面端实现高性能自动化，移动端实现高保真感知。",
    initializeDesktopHub: "初始化桌面中心",
    deployMobileGateway: "部署移动网关",
    becomeHostPartner: "成为宿主合作伙伴",
    footerDesc: "打造全息空间计算与独立 AI 人格的未来。",
    links: "链接",
    resources: "资源",
    newsletter: "新闻订阅",
    newsletterDesc: "随时掌握最新的神经合成突破。",
    allRightsReserved: "版权所有。",
    systemsOnline: "系统在线",
    requestSent: "申请已发送",
    requestCaseStudy: "获取案例研究",
    readyToEvolve: "准备好让您的组织进化了吗？",
    contactSales: "联系销售",
    downloadWhitepaper: "下载白皮书",
    lumiForOrgs: "小枫 企业版",
    highAvailability: "高可用性，冗余架构。",
    premiseIsolation: "100% 本地数据隔离。",
    knowledgeGraph: "跨部门知识图谱。",
    subMillisecond: "亚毫秒延迟，适用于控制回路。",
    predictiveMaintenance: "预测性维护",
    failureForecasting: "AI 驱动的故障预测。",
    autonomousLogistics: "自主物流",
    supplyChainNodes: "自优化供应链节点。",
    federatedLearning: "联邦学习",
    collabTraining: "无需共享数据的协作训练。",
    sovereignAI: "主权 AI",
    regulatoryStandards: "符合严格的监管标准。",
    privacyFirstResearch: "隐私优先研究",
    secureMultiParty: "安全多方计算。",
    lumiCoreRack: "小枫 核心机柜",
    rackUnits: "标准 19 英寸机柜单元，实现无缝数据中心集成。",
    meshProtocolDescLong: "专有低延迟网络，用于大规模节点同步。",
    neuralVault: "神经保管库",
    hardwareEncryption: "硬件级加密，保护组织记忆和知识产权。",
    sound: "声音",
    display: "显示",
    matrixSynthesis: "矩阵合成",
    celestial: "星辉",
    nebula: "星云",
    cyber: "赛博",
    wifi: "无线网络",
    bluetooth: "蓝牙",
    closeNexus: "关闭中枢",
    statusOperational: "状态: 正常运行 / 共享根节点",
    localIntelNode: "本地智能节点",
    serverOnline: "服务器连接: 正常",
    serverDegraded: "服务器: 降级",
    serverOffline: "服务器: 离线",
    bluetoothRequiresDesktop: "蓝牙设备发现需要桌面应用。",
    neuralThroughput: "神经吞吐量",
    synapticLoad: "共振负载",
    meshLatency: "网格延迟",
    optimal: "最佳",
    memoryAllocShards: "内存分配分片",
    activeMeshPeers: "已注册的活跃网格对等节点",
    forceCacheReshard: "强制执行缓存重分片",
    noNeuralNodes: "未发现神经节点",
    searchNeuralHub: "在 小枫 神经中枢中搜索...",
    neuralApp: "神经应用",
    searchHint: "输入以搜索应用、对话与技能",
    searchApps: "应用",
    searchChats: "对话",
    searchChatSub: "历史对话",
    searchSkillsGroup: "技能",
    searchMemoriesGroup: "记忆",
    expandFull: "展开全文",
    collapse: "收起",
    untitledChat: "未命名对话",
    symbioticIntelDesc: "旨在进驻任何物理躯壳的架构，从工业机械到消费级陪伴终端。",
    rapidDataGenesis: "快速数据溯源",
    rapidDataGenesisDesc: "通过数百万宿主采集的高保真情感与物理交互数据，加速 AI 进化。",
    hostMesh: "宿主网格",
    hostMeshDesc: "将世界转化为分布式传感器阵列，让智能赋能本地环境。",
    peakLocalPower: "本地峰值算力",
    cloudLatency: "云端延迟",
    sensorySync: "感官同步",
    meshActiveLabel: "网格激活",
    biometric: "生物特征",
    neuralIDEncrypted: "神经 ID 已加密",
    crossInterfaceContinuity: "跨界面持续性",
    searchShards: "搜索分片...",
    meshStorageUtilization: "网格存储使用率",
    distributedShardLimit: "分布式分片上限",
    folder: "文件夹",
    free: "剩余空间",
    currentMode: "当前模式",
    modeChat: "聊天",
    modeChatTitle: "聊天模式",
    modeChatDesc: "只回答和交流，不主动调用工具、键鼠、命令、团队或运行日志。",
    modeChatHint: "只聊天",
    modeAssistant: "助手",
    modeAssistantTitle: "助手模式",
    modeAssistantDesc: "按任务选择键鼠、命令、工具、技能或团队；复杂任务先提示再执行。",
    modeAssistantHint: "引导执行",
    modeAutoExecute: "自主",
    modeAutoExecuteTitle: "自主模式",
    modeAutoExecuteDesc: "适合多步任务；小枫 会先给行动指南，再用运行日志、桌面控制、命令、工具和团队推进，并展示进度。",
    modeAutoExecuteHint: "自主推进",
    modeAutonomy: "自主",
    modeAutonomyTitle: "自主模式",
    modeAutonomyDesc: "适合多步任务；小枫 会先给行动指南，再用运行日志、桌面控制、命令、工具和团队推进，并展示进度。",
    modeAutonomyHint: "自主推进",
    modeMouseHint: "可见界面控制",
    modeTerminalHint: "文件、代码与命令任务",
    modeAutonomousHint: "多步后台工作",
    meshSyncRate: "网格同步率",
    focusPersonalTerritory: "聚焦个人领地",
    file: "文件",
    edit: "编辑",
    kernel: "内核",
    view: "视图",
    matrix: "矩阵",
    termSession: "终端会话",
    voiceForge: "声音熔炉",
    voiceForgeDesc: "克隆你的数字精髓或从神经预设中选择。",
    startRecording: "开始录音",
    stopRecording: "停止录音",
    recordings: "录音列表",
    voiceName: "声音名称",
    cloneVoice: "克隆声音",
    cloningInProgress: "正在克隆...",
    clonedVoices: "已克隆声音",
    premadeVoices: "预设声音",
    deleteVoice: "删除声音",
    voiceClonedSuccess: "声音克隆成功！",
    voiceDeletedSuccess: "声音已成功删除。",
    noRecordings: "未发现录音。请说话以创建一个。",
    audioVisualizer: "神经音频可视化器",
    cloning: "克隆中",
    personalization: "个性化",
    appearanceThemes: "外观与主题",
    wallpaper: "壁纸",
    selectMatrixVariant: "选择全局矩阵变体",
    desktopBackgroundProtocol: "桌面背景协议",
    wallpaperFusionActive: "壁纸融合已激活",
    standardFocusMode: "标准桌面",
    fusionActive: "融合激活",
    wallpaperMode: "壁纸模式",
    realTimeNode: "实时交互",
    stopSpeaking: "停止播报",
    computePower: "算力状态",
    nodeSync: "节点同步",
    responseLatency: "响应延迟",
    disconnected: "未连接",
    productOverview: "产品概述",
    // Settings — Agent & Neural
    agentFramework: "智能体框架 (小枫 协议)",
    corePersonality: "核心人格",
    observationMode: "观测模式",
    observationModeDesc: "小枫 静静观察，从模式中学习，只在有洞察时发言。",
    observationModeOn: "观测模式已激活 — 小枫 将静静观察并从模式中学习。",
    observationModeOff: "观测模式已关闭 — 小枫 已恢复完整交互。",
    neuralEngine: "神经引擎",
    neuralEngineActive: "活跃",
    autonomousCognitiveLoop: "自主认知循环",
    autonomousCognitiveLoopDesc: "允许智能体基于上下文感知主动执行任务。",
    memoryMesh: "记忆网格",
    memoryMeshSyncing: "同步中",
    persistentLongTermRecall: "持久长期记忆",
    persistentLongTermRecallDesc: "启用在所有已识别节点设备之间共享的跨会话记忆。",
    primaryReasoningBrain: "主推理大脑",
    neuralModelVersion: "神经模型 (版本)",
    proprietaryApiKeyEncrypted: "私有 API 密钥 (已加密)",
    apiKeyOptional: "可选：输入密钥以绕过代理速率限制",
    apiKeyLocalHint: "您的密钥存储在本地，绝不会离开您的安全网格节点。",
    reveal: "显示",
    hide: "隐藏",
    // Settings — API & Media
    neuralApiMatrix: "神经 API 矩阵",
    connectedViaEnv: "已通过环境变量连接",
    noKeyConfigured: "未配置密钥",
    mediaIntegration: "媒体集成",
    connectedLumiMix: "已连接 (小枫 混音)",
    // Settings — Sync & Security
    distributedIntelligenceHub: "分布式智能中枢",
    privacySecurity: "隐私与安全",
    localEncryption: "本地加密",
    localEncryptionDesc: "加密存储在本地磁盘上的所有智能体数据。",
    anonymousMode: "匿名模式",
    anonymousModeDesc: "在协作网络中隐藏您的节点 ID。",
    biometricLock: "生物特征锁",
    biometricLockDesc: "要求指纹或面容 ID 才能生成智能体。",
    desktopNodeRuntime: "桌面节点运行时",
    platform: "平台",
    nodeStatus: "节点状态",
    nodeActive: "活跃",
    hardwareAcceleration: "硬件加速",
    hardwareAccelerationDesc: "使用 GPU 进行神经核心推理。",
    systemTrayMode: "系统托盘模式",
    systemTrayModeDesc: "保持 小枫 在后台运行。",
    // Settings — Hardware
    hardwareSensorNetwork: "硬件传感器网络",
    hardwareSensorNetworkDesc: "小枫 需要访问您的物理传感器以实现现实世界的上下文感知和生物特征验证。所有数据在您的节点本地处理。",
    sensorPermissionIntro: "传感器权限",
    sensorPrimerTitle: "启用 小枫 桌面感知",
    sensorPrimerDesc: "小枫 会使用本地摄像头和麦克风信号来支持在场感知、语音和生物特征能力。能力仍然是桌面端核心体验，这个说明会先告诉你接下来会请求哪些权限。",
    visualAwareness: "视觉感知",
    visualAwarenessDesc: "摄像头权限用于在场状态、人脸识别和手势感知桌面行为。",
    voiceAwareness: "语音感知",
    voiceAwarenessDesc: "麦克风权限用于语音通话、唤醒词检测和可选的声纹录入。",
    localSensorProcessing: "本地优先处理",
    localSensorProcessingDesc: "传感器流用于桌面客户端体验和生物特征校验。你可以随时在设置里查看权限。",
    continueToDesktop: "继续",
    audioReceptors: "音频接收器",
    audioReceptorsDesc: "启用神经语音识别和语音克隆。",
    visualCortex: "视觉皮层",
    visualCortexDesc: "启用多模态视觉和手势控制。",
    privacyAssurance: "隐私保障",
    privacyAssuranceText: "我们的协议严格执行本地处理。未经用户直接签署的授权，您的视觉和听觉数据流绝不会传输到主权网格节点之外。",
    linked: "已链接",
    blocked: "已阻止",
    awaitingAccess: "等待授权",
    retryLink: "重试链接",
    authorize: "授权",
    sensorLinkFailed: "传感器链接失败",
    micAccessSynced: "麦克风访问已同步。",
    camAccessSynced: "摄像头访问已同步。",
    apiKeyRemoved: "API 密钥已移除",
    apiKeySaved: "API 密钥已保存",
    enabled: "已启用",
    disabled: "已禁用",
    save: "保存",
    clear: "清除",
    connected: "已连接",
    // Sidebar groups & items
    sidebarCore: "核心",
    sidebarAiNeural: "AI 与神经",
    sidebarVoiceMedia: "语音与媒体",
    sidebarPersonality: "人格",
    sidebarSystem: "系统",
    sidebarGeneral: "通用",
    sidebarApiMatrix: "API 矩阵",
    sidebarMediaServices: "媒体服务",
    sidebarEditor: "编辑器",
    sidebarMarket: "大厅",
    sidebarSyncHub: "同步中心",
    // MCP
    mcpServers: "MCP 服务",
    mcpScanning: "正在扫描 MCP 协议服务...",
    mcpDescription: "MCP（模型上下文协议）服务通过社区维护的工具扩展智能体能力。启用服务即可让智能体访问文件系统、数据库、Git 等。",
    mcpNoServers: "未配置 MCP 服务",
    mcpAddHint: "请在此设置面板中添加 MCP 服务",
    mcpRestart: "重启",
    mcpConnected: "已连接",
    mcpRestarting: "重启中",
    mcpCrashed: "已崩溃",
    mcpFailed: "已放弃",
    mcpDisconnected: "未连接",
    mcpAvailableServers: "可用 MCP 服务",
    mcpInstallHint: "通过 npm 安装 MCP 服务，或在此设置面板中添加新服务。",
    mcpToolsRegistered: "个工具已注册",
    mcpToolsReconnected: "个工具已重连",
    mcpUpdateFailed: "MCP 更新失败",
    mcpRestartFailed: "重启失败",
    mediaCenterDesc: "语音合成、媒体播放和音频设置。",
    // MCP — Add Server & GitHub Search
    mcpNameCommandRequired: "名称和命令为必填项",
    mcpAutoGenerated: "自动生成",
    mcpAddServerTitle: "添加 MCP 服务",
    mcpServerNamePlaceholder: "服务名称",
    mcpCommandPlaceholder: "命令 (npx, node, python...)",
    mcpArgsPlaceholder: "参数 (空格分隔)",
    mcpAddServerBtn: "添加服务",
    mcpSearchGitHubTitle: "在 GitHub 上搜索 MCP 服务",
    mcpSearchPlaceholder: "搜索 GitHub 上的 MCP 服务...",
    mcpNoResultsFound: "未找到结果：",
    mcpNoDescription: "无描述",
    mcpSearching: "搜索中...",
    mcpSearchBtn: "搜索",
    // MCP — 可用性扫描与状态
    mcpAll: "全部",
    mcpEnabled: "已启用",
    mcpDisabled: "已禁用",
    mcpUpdateSuccess: "内置适配器已更新",
    mcpEnabledState: "已启用",
    mcpDisabledState: "已禁用",
    mcpTestConnection: "测试连接",
    mcpTestOk: "连接正常",
    mcpTestDisabled: "服务已禁用，请先开启开关",
    mcpTestFailed: "测试失败",
    mcpToolsUnit: "个工具",
    mcpScanAll: "全部扫描",
    mcpScanAllTooltip: "测试所有已启用外部服务的连接",
    mcpScanningAll: "扫描中...",
    mcpScanNone: "没有已启用的外部 MCP 服务可扫描",
    mcpScanDone: "扫描完成",
    mcpScanAvailable: "可用",
    mcpScanFailed: "扫描失败，请稍后重试",
    mcpCrashCount: "崩溃",
    mcpCrashUnit: "次",
    mcpLastConnect: "最后连接",
    mcpInfoTitle: "说明",
    mcpInfoDesc: "业务场景适配器（企微 SCRM、飞书、钉钉、IoT、RPA、Agent 协同）已预置集成，打开开关即可启用对应能力。",
    // MCP — 配置弹窗与开关联动
    mcpConfigTitle: "配置",
    mcpConfigSave: "保存配置",
    mcpConfigCancel: "取消",
    mcpConfigSaved: "配置已保存",
    mcpConfigRequired: "请先完成必填项配置",
    mcpConfigNoFields: "该服务无需配置，直接打开开关即可使用",
    mcpConfigBtn: "配置",
    mcpServerAdded: "服务已添加",
    mcpFailedToAddServer: "添加服务失败",
    mcpInstallFailed: "安装失败",
    mcpSearchFailed: "GitHub 搜索失败",
    // Keyboard shortcuts
    keyboardShortcuts: "快捷键管理",
    keyboardSaveHint: "按 Ctrl+S 保存设置",
    keyboardHelpTip: "所有快捷键均可在设置中自定义",
    keyboardEscClose: "按 Esc 关闭弹窗",
    keyboardCtrlSlashOpen: "按 Ctrl+/ 打开帮助",
    // MCP — Desktop & Device Tools
    mcpScreenCaptured: "已截屏",
    mcpScreenDescribed: "屏幕已分析",
    mcpClipboardRead: "已读取剪贴板",
    mcpClipboardWritten: "已复制到剪贴板",
    mcpWindowListed: "已获取窗口列表",
    mcpWindowFocused: "已切换窗口",
    mcpSpeechSynthesized: "语音已合成",
    mcpProactivePushed: "主动消息已推送",
    // Settings — Voice Services
    settingsVoiceServices: "语音服务",
    settingsHardware: "硬件",
    settingsMCP: "MCP",
    voiceServicesApi: "语音服务 API",
    voiceServicesDesc: "语音识别 (STT) 和语音合成 (TTS) 的 API 密钥。小枫 需要这些密钥才能听到你的声音并回应。",
    customWakeWords: "自定义唤醒词",
    customWakeWordsDesc: "添加额外唤醒语音助手的词（例如 \"伙伴\"、\"嘿\"）。最多 20 个，每个最多 20 字。",
    addWakeWord: "添加",
    wakeWordPlaceholder: "输入词语后点击添加...",
    wakeWordMaxReached: "最多 20 个自定义唤醒词",
    // Voice settings toggles
    dashscopeTTS: "DashScope / CosyVoice (文字转语音)",
    dashscopePlaceholder: "输入 DashScope API 密钥...",
    dashscopeHint: "阿里云 DashScope。支持 CosyVoice TTS 和 Qwen LLM。在 dashscope.aliyun.com 获取密钥",
    configured: "已配置",
    keySavedOnServer: "密钥已保存在服务器 (输入以替换)",
    showKey: "显示",
    hideKey: "隐藏",
    keyMaskedPreview: "已保存（脱敏预览）：",
    voiceKeyEitherOr: "豆包语音 与 通义千问/DashScope 是两套备选方案，二选一配置即可；两者都配置时 TTS 优先使用豆包。",
    // STT Usage Monitor
    sttMonitorTitle: "STT 用量监控",
    sttActiveSessions: "活跃会话",
    sttTotalSessions: "总会话",
    sttDuration: "累计时长",
    sttEstimatedCost: "预估费用",
    sttRecentSessions: "最近会话",
    resetStats: "重置",
    autoSpeakReply: "自动语音回复",
    autoSpeakReplyDesc: "开启后，小枫回复时会自动朗读；关闭则仅显示文字，不发声。",
    failedToSaveKey: "保存密钥到服务器失败",
    failedToLoadPersonalities: "加载人格失败",
    failedToLoadProviderStatus: "加载供应商状态失败",
    // Tools panel
    toolControlPanel: "工具控制面板",
    toolsRegistered: "个工具已注册",
    loadingTools: "加载工具中...",
    autoExecute: "自主",
    requireConfirmation: "需确认",
    forbidden: "禁止",
    noToolsInCategory: "此分类中没有工具",
    failedToLoadTools: "加载工具失败",
    loading: "加载中...",
    // Desktop UI — icon labels & widgets
    osKernel: "系统内核",
    llmConfig: "LLM 配置",
    tools: "工具",
    githubMCP: "GitHub MCP",
    personaStats: "人格数据",
    computeCores: "计算核心",
    threads: "线程",
    nodeHost: "节点主机",
    memIndex: "内存指标",
    gbFree: "GB 可用",
    personaPrefix: "人格:",
    memories: "记忆",
    interactions: "交互",
    confidence: "置信度",
    recent: "最近",
    unread: "未读",
    nativeVaultEntry: "本地库入口",
    chatPanelTitle: "小枫 聊天",
    chatPanelEmpty: "输入消息或使用语音开始",
    newConversation: "新建对话",
    newConversationHint: "开始一段新对话",
    noConversations: "暂无对话记录",
    untitled: "未命名",
    today: "今天",
    yesterday: "昨天",
    voiceActive: "语音已激活",
    voiceInactive: "开始语音",
    typeMessage: "输入消息...",
    conversations: "对话列表",
    confirmNewChat: "开始新对话？当前对话将被保存。",
    xiaofengOS: "小枫 OS",
    aiCore: "AI 核心",
    personaLabel: "人格",
    voiceLabel: "语音",
    defaultLabel: "默认",
    llmLabel: "LLM",
    notificationsLabel: "通知",
    webMode: "网页模式",
    battery: "电池",
    charging: "充电中",
    lumiNeuralCore: "小枫 神经核心",
    sessionActive: "会话",
    // Dock & Taskbar tooltips
    activeFocused: "活跃 — 已聚焦",
    openInBackground: "在后台运行中",
    // Kernel Monitor
    autonomyRuntime: "自主运行时",
    activeLabel: "个活跃",
    schedulerNotReporting: "调度器暂无报告",
    nodeReady: "节点就绪",
    kernelServerIp: "服务器",
    kernelLoading: "连接中...",
    kernelDiskUsage: "磁盘",
    kernelNetworkTraffic: "网络流量",
    kernelNetworkLive: "实时速率",
    kernelSwapUsage: "交换分区",
    kernelSystemLoad: "系统信息",
    kernelCpuHistory: "CPU 历史",
    kernelNoData: "采集数据中",
    kernelMountPoint: "磁盘挂载",
    kernelDiskFree: "空闲",
    kernelDockerContainers: "Docker 容器",
    kernelRunning: "运行中",
    // Nexus View
    nexusTitle: "中枢",
    distributedOSCore: "分布式 OS 核心",
    // Devices
    devices: "设备中心",
    deviceMesh: "设备网格",
    // Chat
    chat: "聊天",
    // Lost Protocols
    lostProtocols: "遗落协议",
    // Device Sync Center
    deviceMeshNetwork: "设备网格网络",
    scanningBtn: "扫描中...",
    refreshBtn: "刷新",
    syncedAt: "已同步",
    pairDevice: "配对",
    pairedDevice: "已配对",
    noDevicesConnected: "未连接设备。在其它设备上打开应用即可在此处看到。",
    mobileSensors: "移动传感器",
    sensorPerceptionDesc: "来自设备传感器的实时感知数据。",
    location: "位置",
    motion: "运动",
    liveStreamActive: "实时流已激活",
    sensorsStandby: "传感器待命",
    stopBtn: "停止",
    enableBtn: "启用",
    systemAccess: "系统访问",
    systemAccessLocal: "通过 Tauri IPC 获取本地系统信息。",
    systemAccessWeb: "系统访问需要桌面应用。",
    vaultLabel: "保管库",
    vaultUnlocked: "已解锁",
    vaultWebMode: "网页模式",
    querySystemInfo: "查询系统信息",
    openWebVault: "打开网页保管库",
    readyClickQuery: "就绪 — 点击下方查询系统信息。",
    sensorCtxOnline: "在线",
    sensorCtxAudio: "音频",
    sensorCtxVideo: "视频",
    sensorCtxYes: "是",
    sensorCtxNo: "否",
    // Agent Chat Page
    neuralLink: "神经链接",
    unfinishedConversation: "未完成的对话",
    messagesCount: "条消息",
    continueBtn: "继续",
    newBtn: "新建",
    lastActive: "最近活跃",
    awakePrompt: "你的智能体已觉醒。",
    awakePromptSub: "开始第一次对话。",
    neuralProcessing: "神经处理中...",
    communicatePlaceholder: "与你的数字本质交流...",
    activeCapabilities: "活跃能力",
    webMesh: "网络网格",
    agentStats: "智能体数据",
    logicEngine: "逻辑引擎",
    syncSpeed: "同步速度",
    neuralMeshStatus: "神经网格状态",
    encryptedLinkActive: "加密链路已激活",
    agentSyncDesc: "你的智能体当前与本地节点同步。所有交互都存储在您的私有神经云端。",
    failedToLoadVoices: "加载声音失败",
    speechNotSupported: "此浏览器不支持语音识别。",
    failedToSaveMessage: "保存消息失败",
    failedToRouteNeuralMesh: "无法通过神经网格路由。",
    failedToLoadConversation: "加载对话失败",
    failedToLoadChatHistory: "加载聊天记录失败",
    // Remote MCP Settings
    remoteMCPDevices: "远程 MCP 设备",
    loadingRemoteConfig: "正在加载远程设备配置...",
    remoteMCPDesc: "配置通过 WebSocket 以 MCP 协议连接到 小枫 的远程设备（如 XiaoZhi、智能音箱等）。更改将在服务器重启后生效。",
    noRemoteDevices: "未配置远程设备",
    addRemoteDeviceHint: "添加设备，使其可通过 MCP 调用 小枫 工具",
    addDevice: "添加设备",
    deviceNamePlaceholder: "设备名称（例如 xiaozhi）",
    deviceURLPlaceholder: "wss://device-url/mcp/?token=...",
    saveChanges: "保存更改",
    remoteMCPHowItWorks: "远程设备作为 MCP 客户端通过 WebSocket 连接到 小枫 的 MCP 服务器。小枫 发起 WebSocket 连接，设备发送 MCP 初始化请求，小枫 作为服务器响应 — 暴露诸如 xf_chat、xf_memory_search 和 xf_tool_execute 等工具，设备可以通过语音或其它输入调用这些工具。",
    remoteDeviceEndpointsSaved: "远程设备端点已保存",
    failedToSaveEndpoints: "保存失败",
    // Desktop icon labels
    desktopLabel: "桌面",
    mobileLabel: "移动端",
    arGlassesLabel: "AR 眼镜",
    holographicLabel: "全息",
    browserLabel: "浏览器",
    optimizing: "优化中...",
    // Voice
    voiceFailedToLoad: "加载声音失败",
    // System
    pathLabel: "路径",
    memLabel: "内存",
    osLabel: "操作系统",
    // Navbar
    lumiKernel: "小枫 内核",
    stableVersion: "稳定版 v3.0.0",
    // Sphere controls
    clickToStartSession: "点击开始语音会话",
    sessionActiveClickToEnd: "会话进行中 - 点击结束",
    // Personality Editor
    personalityEditor: "人格编辑器",
    loadingPersonalities: "正在加载人格配置...",
    personalityEditorDesc: "使用结构化身份、边界、表达风格和工具策略来定义 AI 人格。每个人格均可直接使用或按场景覆盖。",
    personalitiesLabel: "人格列表",
    noPersonalitiesDefined: "尚未定义人格。",
    identitySection: "身份",
    coreMotivationLabel: "核心动机",
    behavioralBoundariesSection: "行为边界",
    addBoundary: "添加边界",
    expressionStyleSection: "表达风格",
    personaField: "人格角色",
    toneField: "语气",
    verbosityField: "详细度",
    languagesField: "语言 (逗号分隔)",
    vocabularyHints: "词汇提示",
    addHint: "添加提示",
    toolPolicySection: "工具策略",
    allowedToolsField: "允许的工具 (逗号分隔，* 表示全部)",
    requireConfirmationField: "需要确认 (逗号分隔)",
    forbiddenToolsField: "禁止的工具 (逗号分隔)",
    maxIterationsField: "最大迭代次数",
    perToolOverrides: "单工具安全覆盖",
    memoryPolicySection: "记忆策略",
    retrieveLimitField: "检索上限",
    minConfidenceField: "最低置信度",
    includeTypesField: "包含类型",
    autoExtractLabel: "从对话中自动提取",
    modelsSection: "模型",
    defaultModelField: "默认模型",
    fallbackModelField: "备用模型",
    deleteBtn: "删除",
    selectPersonalityPrompt: "选择一个人格或创建新的",
    idNameRequired: "ID 和名称为必填项",
    saveFailed: "保存失败",
    savedSuffix: "已保存",
    cannotDeleteLumi: "无法删除 'xiaofeng'",
    personalityDeleted: "人格已删除",
    // Agent Generator
    defineIdentity: "1. 定义身份",
    enterAgentName: "输入智能体名称（例如：研究助手）",
    selectNeuralTemplate: "2. 选择神经模板",
    nextDataInfusion: "下一步：数据注入",
    knowledgeBase: "知识库",
    memoryAvatars: "记忆化身",
    avatarStudio: "形象设计室",
    avatarSetAsDesktop: "已设为桌面形象",
    growth: "获客工作台",
    workflow: "工作流",
    fileManager: "文件管理器",
    calendar: "日历",
    screenshot: "截图/录屏",
    stats: "统计",
    exportPet: "导出宠物",
    importPet: "导入社区宠物",
    aiEnhanced: "AI 增强",
    proceduralGen: "程序生成",
    audioRecord: "语音记录",
    audioTranscribing: "转录中...",
    audioTranscriptDone: "转录完成",
    pdfJsonTxtMax: "PDF, JSON, TXT (最大 50MB)",
    vocalEssence: "声音精华",
    cloningEssence: "正在克隆精华...",
    startVoiceSample: "开始声音采样",
    neuralFragments: "神经碎片",
    nextFinalSynthesis: "下一步：最终合成",
    readyForAwakening: "准备觉醒",
    awakeningDesc: "所有神经碎片已准备就绪。合成仪式将把此精华锚定到您的本地节点。",
    nameSummary: "名称",
    templateSummary: "模板",
    dataSummary: "数据",
    fragmentsSuffix: "个碎片",
    selectAgentPrompt: "选择一个智能体以激活或修改其核心。",
    totalPrefix: "总计:",
    delete: "删除",
    chatBtn: "聊天",
    noAgentsGeneratedYet: "尚未生成智能体",
    colleagueSkill: "同事技能包",
    colleagueSkillDesc: "专业知识、工作习惯与协作逻辑。",
    ancestorSkill: "祖先技能包",
    ancestorSkillDesc: "家族历史、智慧与祖先语音合成。",
    kindredMindSkill: "知己技能包",
    kindredMindSkillDesc: "情感共鸣、共享记忆与深度共情。",
    exPartnerSkill: "前任技能包",
    exPartnerSkillDesc: "关系动态、亲密沟通与释怀逻辑。",
    voiceSampleCaptured: "声音样本已采集。前往声音熔炉进行克隆。",
    failedToUploadVoice: "上传声音样本失败",
    uploadFailed: "上传失败",
    recordingSample: "正在录制 5 秒样本...",
    micAccessDenied: "麦克风访问被拒绝",
    // Settings
    englishUS: "English (US)",
    remoteMCPSidebar: "远程 MCP",
    // ── Onboarding ──
    onboardingWelcomeTitle: "欢迎使用 小枫 OS",
    onboardingWelcomeDesc: "本地优先、AI 驱动的操作系统，专为神经计算性能与隐私而设计。",
    onboardingNeuralCore: "神经核心",
    onboardingNeuralCoreDesc: "中心球体是您的入口。点击交互，或使用语音命令控制您的环境。",
    onboardingSpotlight: "聚光灯搜索",
    onboardingSpotlightDesc: "随时按 Cmd+K 召唤聚光灯。查找应用、运行命令、浏览分片目录。",
    onboardingPrivacy: "隐私至上",
    onboardingPrivacyDesc: "所有神经训练和语音处理都在您的本地节点进行。您的数据保持分片和加密。",
    enterWorkspace: "进入工作空间",
    poweredByLumi: "由 小枫 神经核心 v2.0 驱动",
    // ── Workflow ──
    workflowExecuting: "执行工具中...",
    workflowDone: "完成",
    workflowError: "错误",
    workflowIdle: "空闲",
    workflowSteps: "步",
    workflowWaiting: "等待智能体活动...",
    workflowAnalyzing: "正在分析您的请求...",
    workflowCompleted: "已完成",
    workflowCalling: "调用中",
    workflowToolDone: "完成",
    workflowToolFailed: "失败",
    workflowResponseReady: "响应就绪",
    // ── Error ──
    errorSignalInterrupted: "信号中断",
    errorRenderFailure: "组件渲染失败",
    errorUnknown: "未知错误",
    errorRetry: "重试",
    errorReload: "重载",
    errorStackDetail: "堆栈详情（点击展开）",
    errorCopyStack: "复制堆栈",
    // ── Auth ──
    loginTitle: "节点连接",
    register: "创建节点",
    authSubtitle: "小枫 网络认证",
    username: "用户名",
    password: "密码",
    phone: "手机号",
    authPlaceholderUsername: "输入用户名",
    authValidationUsername: "用户名只能包含字母、数字、下划线和横线",
    authUsernameRequired: "请输入用户名",
    authPasswordRequired: "请输入密码",
    authPasswordTooShort: "密码长度至少 6 位",
    authShowPassword: "显示密码",
    authHidePassword: "隐藏密码",
    invalidPhone: "手机号格式不正确",
    loginFailed: "登录失败",
    registerFailed: "注册失败",
    loginBtn: "建立连接",
    registerBtn: "注册节点",
    hasAccount: "返回登录",
    noAccount: "注册新账号",
    rememberMe: "记住我",
    // ── SkillCenter ──
    skillMarketDesc: "浏览、安装和生成技能。每个技能都是一种可复用的能力——小枫 能亲自使用，也能分配给团队成员。",
    skillNoResultsFilter: "无结果。请尝试其他筛选条件。",
    skillCheckBack: "请稍后再来，或自行生成。",
    skillBrowseMarketplace: "浏览大厅或生成技能。",
    // ── AgentChat ──
    chatUserFallback: "用户",
    chatExecuting: "执行中...",
    chatCloseConv: "关闭对话",
    chatHide: "隐藏",
    chatInfo: "信息",
    chatEscClose: "ESC 关闭",
    chatKnowledgeAdded: "已添加到知识库",
    chatMsgs: "条消息",
    chatConnError: "上传连接错误",
    // ── DesktopUI ──
    desktopVersion: "小枫 OS v3.0.0",
    liveDeviceLabel: "在线",
    desktopRefresh: "刷新",
    desktopChangeWallpaper: "更换壁纸",
    desktopArrangeIcons: "排列图标",
    desktopDisplaySettings: "显示设置",
    desktopOpenTerminal: "打开终端",
    desktopRemoveIcon: "从桌面移除",
    desktopProperties: "属性",
    desktopResetWallpaper: "重置默认",
    desktopWallpaperChanged: "壁纸已更新",
    desktopAlwaysOnTop: "始终置顶",
    desktopOpen: "打开",
    windowRestore: "恢复",
    runtimeLog: "运行日志",
    adminCenter: "管理中心",
    // ── App ──
    appInitializing: "小枫 核心 v2.0 初始化中...",
    // ── AgentGenerator ──
    agentSynthesized: "智能体已合成",
    agentCreationFailed: "智能体创建失败",
    synthesisFailed: "合成失败",
    voiceInput: "语音输入",
    stopVoiceInput: "停止语音输入",
    // ── Org ──
    orgWorkbench: "组织",
    orgDashboard: "仪表盘",
    orgKB: "知识库",
    orgTemplates: "智能体模板",
    orgMembers: "成员管理",
    orgSettings: "组织设置",
    orgAudit: "审计日志",
    orgReview: "智能体审核",
    orgChat: "公司 小枫",
    orgJoin: "加入组织",
    orgJoinDesc: "输入邀请码加入组织",
    orgWorkDomain: "工作域",
    orgPersonalDomain: "个人域",
    orgSwitchDomain: "切换域",
    orgConnectionOnline: "在线",
    orgConnectionOffline: "离线",
    orgExitWorkSpace: "退出工作空间",
    orgWorkSpace: "工作空间",
    orgRoleOwner: "拥有者",
    orgRoleAdmin: "管理员",
    orgRoleMember: "成员",
    orgRoleViewer: "观察者",
    // ── Legal (律所) ──
    legalHub: "律所",
    legalBidWorkbench: "标书工作台",
    legalCaseSearch: "类案检索",
    legalAssetTrace: "财产线索",
    legalContractReview: "合同审查",
    legalCaseStrategy: "诉讼策略",
    legalVerifyCitation: "引用校验",
    legalImportJudgment: "导入文书",
    legalBidGenTitle: "标书生成",
    legalBidGenDesc: "上传招标要求 → 生成投标书",
    legalBidGenUpload: "上传招标文件",
    legalBidGenPaste: "或粘贴招标要求",
    legalBidGenPlaceholder: "在此粘贴招标要求...",
    legalBidGenGenerate: "生成标书",
    legalBidGenExport: "导出",
    legalCaseSearchTitle: "类案检索",
    legalCaseSearchDesc: "按案件事实搜索相似裁判文书",
    legalCaseSearchPlaceholder: "描述案件事实...",
    legalCaseSearchSearch: "检索",
    legalCaseSearchResults: "检索结果",
    legalCaseSearchNoResults: "未找到相似案例",
    legalAssetTraceTitle: "财产线索追踪",
    legalAssetTraceDesc: "查询被执行人：企业信息、执行记录、股权结构",
    legalAssetTracePlaceholder: "输入被执行主体名称...",
    legalAssetTraceSearch: "追踪",
    legalContractReviewTitle: "合同审查",
    legalContractReviewDesc: "上传合同，标注风险条款并引用判例",
    legalContractReviewPlaceholder: "粘贴合同文本或上传文件...",
    legalContractReviewReview: "审查",
    legalContractReviewRisks: "风险项",
    legalCaseStrategyTitle: "诉讼策略",
    legalCaseStrategyDesc: "输入事实 → 输出策略：适用法条、参考案例、风险分析",
    legalCaseStrategyPlaceholder: "详细描述案件事实...",
    legalCaseStrategyAnalyze: "分析",
    legalVerifyCitationTitle: "引用校验",
    legalVerifyCitationDesc: "验证法条/案例引用是否真实有效",
    legalVerifyCitationPlaceholder: "粘贴含有引用（《...》）的文本...",
    legalVerifyCitationVerify: "校验",
    legalImportJudgmentTitle: "导入裁判文书",
    legalImportJudgmentDesc: "将裁判文书（PDF/DOCX）导入知识库",
    legalSource: "来源",
    legalStatus: "状态",
    legalScore: "相似度",
    // ── DesktopUI missing ──
    dailyCapability: "每日能力",
    mediaServices: "媒体服务",
    voiceStudio: "语音工作室",
    voiceStudioDesc: "克隆与设计",
    voiceDesignTab: "语音设计",
    voiceDesignDesc: "描述你想要的语音，AI 将为你生成。无需音频样本。",
    voiceDesignPrompt: "语音描述",
    voiceDesignPlaceholder: "例如：一个温暖、温柔的女声，语气柔和，语速适中，适合讲故事...",
    voiceDesignName: "语音名称",
    generateVoice: "生成语音",
    // ── Boot ──
    bootProgress: "启动进度:",
    bootCoreTemp: "核心温度: 34°C",
    bootMeshConn: "网格连接: 100%",
    // Docs
    gettingStarted: "快速入门",
    apiReference: "API 参考",
    docsTagline: "掌握 小枫 协议，构建高级智能体架构。",
    searchDocs: "搜索文档...",
    hostArchitecture: "宿主架构",
    hostArchitectureDesc: "智能宿主计划定义了一种硬件无关的通信协议，使 小枫 神经核心能够'寄居'于现有的物理系统中。",
    neuralEncapsulationLabel: "神经封装：",
    neuralEncapsulationDesc: "将现有 API 封装为 小枫 兼容的意图流。",
    symbioticFeedbackLabel: "共生反馈：",
    symbioticFeedbackDesc: "来自宿主的实时感官数据促进本地智能体的进化。",
    protocolStatusBeta: "协议状态：Beta",
    activeHostsStats: "活跃宿主：12,400+ | 类别：玩具、工业、可穿戴设备",
    syncMultiDesc: "在所有维度之间无缝同步。",
    desktopPlatforms: "Windows / macOS / Linux (Electron 运行时)",
    desktopHardwareAccess: "直接硬件访问，支持本地推理",
    mobilePlatforms: "iOS / Android (Capacitor 运行时)",
    multimodalSensing: "多模态感知与语音同步",
    brainNode: "脑节点",
    senseNode: "感知节点",
    gettingStartedDesc: "了解智能体生成和本地节点设置的基础知识。",
    quickStart: "快速开始",
    quickStartDesc: "不到5分钟即可上手运行。",
    ecosystemDoc: "生态系统",
    ecosystemDocDesc: "探索 小枫 分布式网络。",
    installDoc: "安装",
    firstAgentDoc: "第一个智能体",
    syncingDevicesDoc: "同步设备",
    cliSetupDoc: "CLI 设置",
    helloWorldDoc: "Hello World",
    nodeConfigDoc: "节点配置",
    meshBasicsDoc: "网格基础",
    p2pSyncDoc: "P2P 同步",
    securityDoc: "安全",
    apiReferenceDesc: "深入了解 小枫 本地 SDK 和神经合成 API。",
    authDoc: "身份验证",
    dataStreamsDoc: "数据流",
    memoryCoreDoc: "记忆核心",
    cliCommands: "CLI 命令",
    cliCommandsDesc: "小枫 命令行界面的完整参考。",
    securityApi: "安全 API",
    securityApiDesc: "管理加密和节点权限。",
    keyManagementDoc: "密钥管理",
    accessControlDoc: "访问控制",
    privacyLevelsDoc: "隐私级别",
    advancedGuides: "高级指南",
    advancedGuidesDesc: "构建复杂的多智能体系统和自定义知识图谱。",
    orchestrationDoc: "编排",
    customLLMsDoc: "自定义 LLM",
    securityRulesDoc: "安全规则",
    performanceDoc: "性能优化",
    performanceDocDesc: "优化您的节点以实现高速推理。",
    gpuAccelerationDoc: "GPU 加速",
    memoryManagementDoc: "内存管理",
    cachingDoc: "缓存",
    globalMeshDoc: "全局网格",
    globalMeshDocDesc: "跨分布式网格扩展您的智能体。",
    swarmLogicDoc: "集群逻辑",
    crossNodeSyncDoc: "跨节点同步",
    latencyDoc: "延迟",
    neuralSynthesisProtocol: "神经合成协议",
    neuralSynthesisProtocolDesc: "小枫 协议使用分布式神经合成来构建智能体智能。此过程完全在本地进行，确保您的数据永远不会离开您的节点。",
    neuralSynthesisPrivacyDesc: "通过定义高隐私级别，协议将对所有神经权重计算使用高级加密和纯本地处理。",
    securityPrivacy: "安全与隐私",
    securityPrivacyDesc: "隐私是 小枫 的核心。我们的安全架构建立在三个支柱之上：",
    localFirstProcessing: "本地优先数据处理",
    endToEndEncryption: "端到端节点加密",
    decentralizedIdentity: "去中心化身份 (DID)",
    // Profile
    nodeId: "节点 ID",
    verifiedNode: "已验证节点",
    levelArchitect: "12级架构师",
    earlyExplorer: "早期探索者",
    withdraw: "提取",
    topUp: "充值",
    nodeStats: "节点统计",
    totalData: "总数据量",
    nodePower: "节点算力",
    status: "状态",
    operational: "运行中",
    users: "用户",
    agents: "智能体",
    latency: "延迟",
    // JoinUs
    phaseEcosystemGenesis: "阶段 01：生态创世",
    activeNode: "活跃节点",
    rewardProtocol: "奖励协议",
    soulCreditsDesc: "灵魂积分 (SC) 根据在线时长和计算质量铸造。",
    globalMeshStatus: "全球网格状态",
    meshNodeCount: "14,204 节点",
    meshDistributedDesc: "分布在 82 个国家。网格总算力：4.2 PFLOPS。",
    compute: "算力",
    computeDesc: "闲置 GPU 算力。",
    ethics: "伦理",
    ethicsDesc: "治理。",
    curator: "策展人",
    curatorDesc: "数据验证。",
    advocate: "倡导者",
    advocateDesc: "增长。",
    nodeSimulator: "节点模拟器",
    soulCreditsPerDay: "灵魂积分/天",
    nodeSimulatorDesc: "根据您的硬件配置和网络延迟估算您的贡献价值。",
    startBenchmarking: "开始基准测试",
    quickAction: "快捷操作",
    directContact: "直接联系",
    adminConfig: "管理员配置",
    cancel: "取消",
    // ── Mobile Platform ──
    navHome: "首页",
    navFactory: "工厂",
    navCore: "核心",
    navAgents: "智能体",
    navProfile: "个人",
    deviceStatus: "设备状态",
    activeNodeMonitoring: "活跃节点监控",
    localHost: "本地主机",
    meshBridge: "网格桥接",
    shardingSync: "分片同步",
    globalTraffic: "全球流量",
    biometricsTile: "生物识别",
    telemetryTile: "遥测",
    centralPersona: "中央人格",
    localShardIntegrityVerified: "本地分片完整性已验证",
    initializeSync: "初始化同步",
    exitMobileInterface: "退出移动界面",
    neuralEntities: "神经实体",
    activeStatus: "活跃",
    // ── Desktop Intro ──
    backToEcosystem: "返回生态系统",
    xiaofengOSStableRelease: "小枫 OS v2.4.1 稳定版",
    highPerfWorkstationNode: "高性能工作站节点",
    initializeKineticDesktopCore: "初始化 KINETIC 桌面核心",
    desktopHeroDesc: "将您的硬件转变为主权神经节点。零延迟，本地 LLM 执行，内核级 OS 自动化。",
    bootVirtualOS: "启动虚拟 OS",
    browserDemoInstance: "浏览器演示实例",
    downloadNative: "下载原生版",
    stableReleaseLabel: "稳定版 V2.4.1",
    accelerationLabel: "加速",
    kernelLoadSequence: "内核加载序列",
    stableLabel: "稳定版",
    nativeNodeSuperiority: "原生节点优势",
    desktopSuperiorityDesc: "为什么桌面客户端是神经架构师的首选",
    standardBrowserEdition: "标准浏览器版",
    browserItem1: "依赖云端的神经同步",
    browserItem2: "受限沙盒环境",
    browserItem3: "标准 CPU/GPU 限制",
    browserItem4: "通用兼容性",
    lumiNativeKernelNode: "小枫 原生内核节点",
    kernelItem1: "完整的芯片级本地 LLM 执行",
    kernelItem2: "直接操作系统和文件系统自动化",
    kernelItem3: "硬件加速（DirectX/Metal）",
    kernelItem4: "主权数据保险库（无遥测）",
    featureNvidiaAccel: "NVIDIA/Metal 加速",
    featureNvidiaAccelDesc: "优化内核以最大化本地推理速度，充分利用硬件性能。",
    featureZeroKnowledge: "零知识同步",
    featureZeroKnowledgeDesc: "所有个人数据保留在您的本地保险库中。只有元数据分片进入网络。",
    featureOSAutomation: "OS 级自动化",
    featureOSAutomationDesc: "通过神经命令直接控制系统、文件和应用。",
    systemBinaryDownloads: "系统二进制下载",
    selectArchitecture: "选择您的架构以开始部署",
    stableReleaseBadge: "稳定版",
    initDownload: "开始下载",
    // ── Mobile Intro ──
    lumiMobileSyncActive: "小枫 Mobile Sync v1.2.0 运行中",
    portableNeuralGateway: "便携神经网关",
    neuralSensoryPerception: "神经感知",
    mobileHeroDesc: "您的智能体，脱离工作站。分布式网格网络与实时感知分片相结合。",
    launchMobileHub: "启动移动中枢",
    betaPreview: "测试版预览",
    cameraStream: "摄像头流",
    sensors: "传感器",
    activeStatusCap: "活跃",
    mobileHub: "移动中枢",
    realtimeSensorySharding: "实时感知分片",
    environmentMetadata: "环境元数据",
    capturing: "捕获中...",
    mobileBinaryPackages: "移动二进制包",
    deployMobileClient: "将 小枫 客户端部署到您的便携设备",
    releasedBadge: "已发布",
    initInstall: "开始安装",
    quickScan: "快速扫码",
    scanQRCode: "使用您的移动设备扫描二维码以打开去中心化安装网关。",
    gatewayOnline: "网关在线",
    meshOnline: "在线",
    // ── Knowledge Base ──
    awakening: "觉醒中...",
    searchMemories: "搜索记忆...",
    noMatchesFound: "未找到匹配项",
    kbImport: "导入",
    kbOrganize: "整理",
    kbMerge: "合并",
    kbReflect: "反思",
    kbPatterns: "模式",
    kbFiles: "文件",
    kbMem: "记忆",
    kbBranches: "分支",
    kbEscHint: "ESC 关闭 · 点击节点查看 · 拖拽旋转",
    kbDeleted: "已删除",
    kbDeleteFailed: "删除失败",
    kbIngested: "已收录",
    kbIngestFailed: "收录失败",
    kbProtected: "已保护",
    kbUnprotected: "已取消保护",
    kbProtectFailed: "保护失败",
    kbPromoteConfirm: "提升为核心身份？",
    kbTierChanged: "层级已变更",
    kbUpdated: "已更新",
    kbUpdateFailed: "更新失败",
    kbUploadedFiles: "文件已上传",
    kbUploadFailed: "上传失败",
    kbOrganizationFailed: "整理失败",
    kbConsolidated: "已合并",
    kbConsolidationFailed: "合并失败",
    kbReflectionComplete: "反思完成",
    kbReflectionFailed: "反思失败",
    kbPatternsFound: "发现模式",
    kbNoNewPatterns: "无新模式",
    kbAnalysisFailed: "分析失败",
    kbDeleteConfirm: "删除",
    kbNotEnoughMemories: "没有足够的未整理记忆",
    kbNeedMemories: "需要记忆",
    kbNothingToReflect: "没有可反思的内容",
    kbEnterAgentId: "输入智能体 ID：",
    // ── Voice Forge ──
    neuralSynthesisActive: "神经合成已激活",
    secRecommended: "/ 建议15-30秒",
    vfOptimal: "最佳",
    keepSpeaking: "继续说话——神经图谱需要更多数据",
    goodMoreSeconds: "不错——再录制几秒以获得最佳采集效果",
    neuralPatternsCaptured: "神经模式已捕获——准备好后停止",
    recordingComplete: "优秀！录制完成——停止以保存",
    speakNaturally: "自然说话15-30秒以获得最佳采集效果。",
    orDivider: "或",
    uploadAudioFile: "上传音频文件",
    vfClearAll: "清空全部",
    sampleLabel: "样本",
    kbWebMOpus: "KB · WebM Opus",
    voiceClonedSuccessStatus: "声音克隆成功！",
    voicesLimit: "/ 10 上限",
    playbackFailed: "播放失败",
    failedToPlaySample: "播放语音样本失败",
    recordingCaptured: "录音已捕获",
    enterVoiceName: "请输入您的声音本质名称。",
    addedFiles: "已添加文件",
    // ── Skill Center ──
    flagshipSkill: "旗舰技能",
    featuredTab: "精选",
    installedStatus: "已安装",
    installingStatus: "安装中...",
    installStatus: "安装",
    requiresApiKey: "需要",
    getApiKey: "获取 API Key",
    tryPrompt: "试试：",
    setupRequired: "需要手动配置",
    // ── Chat Panel ──
    closeConversation: "关闭对话",
    voice: "语音",
    cancelTask: "取消任务",
    stop: "停止",
    // ── Notification Center ──
    ncTitle: "通知中心",
    unreadCount: "未读",
    allCaughtUp: "全部已读",
    ncEmpty: "暂无通知",
    systemEventsHere: "系统事件和提醒将显示在这里",
    // ── Tool Confirm Dialog ──
    toolAuthorization: "工具授权",
    toolExplicitPermission: "此工具需要您的明确授权",
    highRiskAction: "高风险操作",
    highRiskActionNote: "此请求不支持自动批准和始终允许。",
    confirmAction: "确认操作",
    confirmActionNote: "此操作可能更改应用状态、文件、剪贴板或桌面。",
    lowRiskAction: "需要权限",
    lowRiskActionNote: "请检查请求后再允许小枫继续。",
    autoApproveDesc: "启用后自动批准所有工具。禁用则恢复逐个确认。",
    highRiskAutoApproveDisabled: "自动批准不适用于高风险操作",
    alwaysAllow: "始终允许",
    highRiskAlwaysDisabled: "高风险操作不能始终允许",
    moreToolsWaiting: "个更多工具等待中",
    moreToolsWaitingPlural: "个更多工具等待中",
    deny: "拒绝",
    allow: "允许",
    // ── Voice Call Button ──
    callStart: "开始",
    callConnecting: "连接中...",
    callListening: "聆听中",
    callThinking: "思考中",
    callSpeaking: "说话中",
    callQueued: "排队中",
    callPassive: "待机",
    endCall: "结束通话",
    startVoiceCall: "开始语音通话",
    // ── Proactive Notifications ──
    notifLumi: "小枫",
    notifReminder: "提醒",
    notifMemoryAlert: "记忆警报",
    notifDailySummary: "每日摘要",
    notifEveningWrapup: "晚间总结",
    notifBehavioralInsight: "行为洞察",
    notifToolFailed: "工具失败",
    notifToolSuccess: "工具",
    notifRunningTool: "运行工具",
    // ── Org Template Creator ──
    templateSubmitted: "智能体模板已提交！",
    templatePendingReview: "智能体模板已进入管理员审核，通过后团队成员可安装到 小枫 团队。",
    backToMarketplace: "返回智能体模板",
    submitTemplate: "提交智能体模板",
    templateDesc: "从你的智能体创建可复用模板，提交给组织审核",
    templateName: "模板名称",
    iconLabel: "图标",
    briefDescription: "简要描述此智能体的功能...",
    agentConfigJSON: "智能体配置 (JSON)",
    submitForReview: "提交审核",
    categoryProductivity: "生产力",
    categoryDataAnalysis: "数据分析",
    categoryCustomerSupport: "客户支持",
    categoryEngineering: "工程",
    categoryHR: "人力资源",
    categorySales: "销售",
    categoryCreative: "创意",
    categoryOther: "其他",
    // ── Org Knowledge Base Editor ──
    editArticle: "编辑文章",
    newArticle: "新建文章",
    articleTitle: "文章标题...",
    tagsCommaSeparated: "标签（逗号分隔）",
    writeArticleContent: "在此撰写文章内容... 支持 Markdown。",
    createArticle: "创建文章",
    updateArticle: "更新文章",
    backToKB: "返回知识库",
    catGeneral: "通用",
    catPolicy: "政策",
    catSOP: "标准操作流程",
    catProduct: "产品",
    catCulture: "文化",
    catHR: "人力资源",
    catTechnical: "技术",
    draftStatus: "草稿",
    publishedStatus: "已发布",
    // ── Personality Evolution ──
    personalityEvolution: "人格进化",
    evolve: "进化",
    noEvolutionHistory: "暂无进化历史。小枫 的人格随着您一起成长。",
    ownerProfile: "主人画像",
    dominantTone: "主导语气：",
    formalityLabel: "正式度：",
    expressiveness: "表现力：",
    memoriesAnalyzed: "已分析记忆：",
    mutations: "突变",
    selectEvolutionStep: "选择一个进化步骤以查看详情",
    plasticity: "可塑性：",
    cooldown: "冷却时间：",
    maxMutations: "最大突变数：",
    minMemories: "最少记忆数：",
    failedToLoadEvolution: "加载进化数据失败",
    lumiEvolvedTo: "小枫 进化为",
    personalityEvolved: "人格已进化！",
    // ── Personality Editor ──
    ttsVoice: "TTS 语音",
    defaultVoice: "默认",
    evolutionNotInit: "进化向量尚未初始化。将在首次交互时播种。",
    noBoundariesDefined: "没有定义边界。",
    // ── Memory Avatar Lab ──
    uploadChatLogFirst: "请先上传聊天记录",
    // ── Unified Agent ──
    noActiveSensors: "无活跃传感器",
    sensorAPIUnavailable: "传感器 API 不可用",
    failedToLoadAgentConfig: "加载智能体配置失败",
    // ── Global Node Map ──
    devicesOnline: "设备在线",
    shardClusterAlpha: "分片集群 Alpha",
    meshBridgeV4: "网格桥接 v4",
    // ── Desktop UI ──
    pixelleDesc: "AI 图像和视频生成",
    minimaxDesc: "音乐、视频、图像和语音 AI",
    desktopCommanderDesc: "通过 AI 实现完整桌面控制",
    videoForgeDesc: "视频和音频编辑套件",
    webFetcherProDesc: "智能网页内容提取",
    codeSandboxDesc: "在云沙箱中运行 Python 和 JS",
    installFailed: "安装失败",
    // ── Voice Picker ──
    searchVoices: "搜索语音...",
    selectVoice: "选择语音",
    voiceLoading: "加载中...",
    voiceRetry: "重试",
    voiceRetrying: "重试中...",
    premadeLabel: "预设",
    clonedLabel: "已克隆",
    // ── Mode Switch Dialog ──
    confirmModeSwitch: "确认模式切换",
    enterMode: "进入模式",
    modeMeetingConfirmNote: "会议模式将自动开启语音转文字、记录会议笔记，结束后可生成纪要和分析报告。",
    modeAutoConfirmNote: "自主模式可使用工具、运行日志、团队、命令和桌面控制；进度可见，敏感操作仍需确认。",
    // ── Voice Training Dialog ──
    selectAudioSample: "请至少选择一个音频样本",
    enterVoiceNameRequired: "请输入语音名称",
    onlyAudioFilesAccepted: "仅接受音频文件",
    voiceLab: "语音实验室",
    voiceLabDesc: "克隆和训练神经语音",
    dropAudioHere: "拖拽音频文件到此处",
    dropAudioHint: "点击或拖拽 — WAV, MP3, OGG",
    analyzingSamples: "分析音频样本中...",
    nextDefineCharacter: "下一步：定义角色",
    identityDesignation: "身份命名",
    engineArchitecture: "引擎架构",
    cosyVoiceEngineDesc: "CosyVoice 是支持的自定义声音克隆引擎。克隆前请在语音服务中添加 DashScope API Key。",
    cloningArchetype: "正在克隆声音...",
    initiateNeuralCloning: "开始神经克隆",
    synthesizingNeuralMap: "正在合成神经图谱",
    synthesizingDesc: "正在将您的声纹特征映射到新的神经声音原型。",
    archetypeInitialized: "声音原型已初始化",
    archetypeDesc1: "声音原型 ",
    archetypeDesc2: " 已在您的神经网络中激活。",
    // ── Node Detail Panel ──
    editContentPrompt: "编辑内容：",
    // ── Neural Synthesis Monitor ──
    noSensorData: "无传感器数据",
    // ── Subscription Panel ──
    contactAdminUpgrade: "联系管理员升级",
    // ── Sanctuary ──
    defaultMemoryLabel: "记忆",
    defaultYouLabel: "你",
    sanctuaryLabel: "圣域",
    // ── Org Template Marketplace ──
    templateMarketplace: "智能体模板市场",
    templateMarketplaceDesc: "把组织审核通过的子 agent 安装到 小枫 团队",
    searchTemplates: "搜索模板...",
    allCategoriesFilter: "全部分类",
    noTemplatesFound: "未找到模板",
    numInstalls: "次安装",
    installTemplate: "安装为团队智能体",
    templateAdded: "已安装到 小枫 团队",
    installingTemplate: "安装中...",
    // ── Org Template Review Queue ──
    templateReviewQueue: "智能体模板审核",
    templatesPendingReview: "个智能体模板待审核",
    allTemplatesReviewed: "所有智能体模板已审核！",
    reviewComment: "审核评论...",
    approve: "批准",
    reject: "拒绝",
    review: "审核",
  }
};
